import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import filePdfO from '@iconify/icons-fa/file-pdf-o'
import fileTextO from '@iconify/icons-fa/file-text-o'
import fileExcelO from '@iconify/icons-fa/file-excel-o'
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  XMarkIcon,
  TrashIcon,
  EyeIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  TruckIcon,
  PrinterIcon,
  DocumentArrowDownIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import {
  MainPage,
  PageTitle,
  PrimaryButton,
  SecondaryButton,
  ModalQuestion,
  Modal,
  FormText,
  FormSelect,
  FormCheckbox,
  PaginationFooter,
  DispatchCreationFlow,
  DispatchPrintModal,
  ShipmentLabelDjangoModal,
  ShipmentMultiPrintModal,
  ModalLoading,
  ConfirmModalStatus,
  Spinner,
} from '../../components'
import Dropdown from '../../components/Atoms/Dropdown'
import { ImportProgressBar } from '../../components/ShipmentBulkUpload/ImportProgressBar'
import { useBulkUploadProgress } from '../../hooks/useBulkUploadProgress'
import { MissingGuidesModal } from '../../components/Dispatch/MissingGuidesModal'
import { getManifestsByShipment } from '../../services/DispatchServices'
import { ManifestByShipmentDto } from '../../interfaces/Django/ManifestByShipmentDto'
import { useAppSelector } from '../../store/hooks'
import {
  getImport,
  getImportFile,
  cancelImport,
  deleteImport,
  executeImport,
  deleteImportDetail,
  createBulkUploadManifest,
} from '../../services/ShipmentBulkUploadServices'
import { alertService } from '../../services'
import { formatDateTime } from '../../utils/format'
import {
  exportTablePDF,
  exportTableXLSX,
  exportTableTXT,
  exportFormatoShipmentXLSX_V2WithCoords,
} from '../../utils/exports'
import { shipmentGroupsToTemplateRows } from '../../utils/bulkTemplateRows'
import { BULK_CORRECTION_EXTRA_COLUMNS } from '../../utils/bulkCorrectionColumns'
import {
  payloadJsonToShipmentGroup,
  detailPartnerId,
} from '../../utils/shipmentPayload'
import {
  BulkPrintRow,
  ZebraFailure,
  printableRowsFromDetails,
  chunkPrintRows,
  degradeBatchSize,
  runZebraPrint,
} from '../../utils/bulkPrint'
import { useBulkPrintSelection } from '../../hooks/useBulkPrintSelection'
import { getShipmentZebraLabel } from '../../services/ShipmentServices'
import { sendLabelToPrinter } from '../../services/printerServices'
import { sanitizeForExcel } from '../../utils/bulkTemplateDisplay'
import { useBulkTemplateArgs } from '../../hooks/useBulkTemplateArgs'
import {
  headerStatusMeta,
  detailStatusMeta,
  canDeleteDetailStatus,
  resolveDetailStatusName,
  messageForCode,
  getBulkActionFlags,
  executedGuideNumbers,
  resolveHeaderStatusName,
  SERVER_ERROR_RETRY_CAP,
  detailKey,
  detailLabel,
  logsCaption,
  logRowLabel,
  annulmentLogView,
  logStaleReason,
  logEventLabel,
} from '../../utils/shipmentBulkUploadStatus'
import { DispatchGuideDto } from '../../interfaces'
import {
  getPendingManifestMark,
  savePendingManifestMark,
  clearPendingManifestMark,
  PendingManifestMark,
} from '../../utils/pendingManifestMark'
import {
  ImportDetailDTO,
  ImportHeaderDTO,
  ImportHeaderStatusName,
  ImportDetailStatusName,
  ImportProgressDTO,
  hasManifest,
} from '../../interfaces/Import/ShipmentBulkUploadApi'
import {
  BULK_UPLOAD_DENIED_MESSAGE,
  ownsBulkUpload,
} from '../../utils/bulkUploadOwnership'

/**
 * Mínimo y máximo del motivo de eliminación. Espejo de
 * ShipmentBulkUploadDeleteRequestDTO en el backend: 10 caracteres para que "error"
 * no pase, y 150 porque el motivo termina en Approval.observation, que el flujo de
 * anulación existente ya limita a 150.
 */
const DELETE_REASON_MIN = 10
const DELETE_REASON_MAX = 150

const LIST_ROUTE = '/shipment/bulk-upload/list'

/**
 * Cuántas guías entran en un grupo de impresión. **Uno solo para los cinco formatos.**
 *
 * Antes había tres números distintos (300 etiquetas PDF, 100 bolsillo, 100 tope Zebra) y
 * eso obligaba al operador a aprenderse una regla por botón. Ahora un solo número vale
 * para todo: los tres caminos parten la selección igual y la cuentan igual.
 *
 * Ninguno de los tres puede mandarse entero:
 * - PDF: `apiDjango` corta a los 30 s y las etiquetas se renderizan con WeasyPrint,
 *   que tarda por guía.
 * - Bolsillo: cada guía es un documento de página completa en una sola petición .NET.
 * - Zebra: es UNA petición por guía contra el agente local, en secuencia.
 *
 * HISTORIAL DEL NÚMERO — es empírico, no teórico:
 * - 300: heredado, nunca medido.
 * - 100: seguía dando timeout en `POST /label` (se veía como "Error HTTP  al generar la
 *   etiqueta.", sin número de status, que es la firma de un timeout de axios y no de un
 *   error del servidor).
 * - 50: valor actual, reportado como estable (2026-09-08).
 *
 * Si vuelve a aparecer ese mensaje, bajar de a 50. Subirlo exige medir primero.
 */
const PRINT_BATCH_LIMIT = 50

/**
 * A cuánto se baja el grupo cuando el servicio se queda sin tiempo.
 *
 * El tope de arriba es una apuesta razonable, no una garantía: cuánto aguanta el backend
 * depende de la carga del momento y de cuántas piezas tenga cada guía. En vez de dejar
 * al operador probando números a mano, ante un timeout la pantalla vuelve a partir la
 * selección con este valor y reintenta sola.
 *
 * Es el piso: si con 10 tampoco genera, el problema no es el tamaño del grupo y se le
 * dice que recargue y escale.
 */
const PRINT_BATCH_MIN = 10

/** Los cinco formatos que ofrecen la cabecera y la barra de selección. */
type BulkPrintKind =
  | 'pdf'
  | 'pdf-letter'
  | 'pocket'
  | 'zebra-10x6'
  | 'zebra-10x10'

/** FormatType que espera el servicio de etiquetas: 0 = 10x6, 1 = 10x10. */
const ZEBRA_FORMAT_TYPE: Record<'zebra-10x6' | 'zebra-10x10', 0 | 1> = {
  'zebra-10x6': 0,
  'zebra-10x10': 1,
}

/**
 * Mensaje de cierre de una tanda Zebra.
 *
 * LISTA los números que fallaron en vez de contarlos: un "12 fallaron" no le sirve de
 * nada al operador, que necesita saber CUÁLES para reimprimirlas. Se muestran hasta 15
 * para no romper el modal, con el resto resumido.
 */
function buildZebraResult(
  result: {
    printed: string[]
    failures: ZebraFailure[]
    aborted: boolean
    abortReason?: 'cancelled' | 'unmounted' | 'agent-down'
  },
  batch?: { group: number; totalGroups: number },
): { status: ConfirmModalStatus; title: string } {
  const { printed, failures, aborted, abortReason } = result

  // Con un solo grupo el prefijo sería ruido; con varios es lo que ubica al operador.
  const prefix =
    batch && batch.totalGroups > 1
      ? `Grupo ${batch.group} de ${batch.totalGroups}: `
      : ''

  /**
   * Antepone el grupo y arregla la mayúscula: con prefijo la frase va en minúscula
   * después de los dos puntos, y sin prefijo tiene que arrancar en mayúscula.
   */
  const line = (body: string) =>
    prefix ? `${prefix}${body}` : body.charAt(0).toUpperCase() + body.slice(1)

  const failedList = failures.map((f) => f.shipmentNumber)
  const shown = failedList.slice(0, 15).join(', ')
  const rest = failedList.length > 15 ? ` y ${failedList.length - 15} más` : ''
  const failDetail = failedList.length
    ? `\n\nNo se imprimieron: ${shown}${rest}.`
    : ''

  /**
   * POR QUÉ fallaron, que es lo único accionable.
   *
   * Hasta acá el motivo solo se mostraba en el corte por `agent-down`: cuando la
   * etiqueta no se podía OBTENER (permiso faltante, guía sin servicio u oficina para el
   * formato 10x6, servicio externo caído) el operador veía "no se pudo imprimir ninguna"
   * y una lista de números, sin una sola pista de qué arreglar — y el mensaje que Django
   * ya había devuelto se descartaba.
   *
   * Se deduplican porque una tanda de 40 guías falla casi siempre por la MISMA causa, y
   * repetirla 40 veces enterraría la lista de guías.
   */
  const reasons = Array.from(
    new Set(failures.map((f) => f.message).filter(Boolean)),
  )
  const reasonDetail = reasons.length
    ? `\n\nMotivo: ${reasons.slice(0, 3).join(' — ')}${
        reasons.length > 3 ? ` (y ${reasons.length - 3} motivo(s) más)` : ''
      }`
    : ''

  // El agente apagado no es "algunas fallaron": es una sola causa que explica todo lo
  // que falta, y el operador puede arreglarla y reintentar.
  if (abortReason === 'agent-down') {
    const reason =
      failures.find((f) => f.reason === 'agent-down')?.message ??
      'No se pudo comunicar con la impresora.'
    return {
      status: ConfirmModalStatus.ERROR,
      title: `${prefix}${reason}\n\nSe imprimieron ${printed.length} etiqueta(s) antes de cortar. Abrí el programa de impresión y volvé a intentarlo con las que faltan.${failDetail}`,
    }
  }

  if (aborted && abortReason === 'cancelled') {
    return {
      status: ConfirmModalStatus.WARNING,
      title: line(
        `impresión cancelada. Se imprimieron ${printed.length} etiqueta(s).${reasonDetail}${failDetail}`,
      ),
    }
  }

  if (failures.length === 0) {
    return {
      status: ConfirmModalStatus.SUCCESS,
      title: line(`se imprimieron ${printed.length} etiqueta(s).`),
    }
  }

  if (printed.length === 0) {
    return {
      status: ConfirmModalStatus.ERROR,
      title: line(
        `no se pudo imprimir ninguna de las ${failures.length} etiqueta(s).${reasonDetail}${failDetail}`,
      ),
    }
  }

  return {
    status: ConfirmModalStatus.WARNING,
    title: line(
      `se imprimieron ${printed.length} de ${printed.length + failures.length} etiqueta(s).${reasonDetail}${failDetail}`,
    ),
  }
}

function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

/** Etiqueta del dueño de la carga: nombre resuelto (sitio/cuenta) o, si falta, el ID. */
function ownerLabel(h: ImportHeaderDTO): string {
  if (h.billing_account_name) return h.billing_account_name
  if (h.account_name) return h.account_name
  if (h.billing_account) return `Sitio ${h.billing_account}`
  if (h.account_id) return h.account_id
  return '–'
}

const ShipmentBulkUploadDetail: FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const headerId = id ?? null

  const user = useAppSelector((s) => s.user)

  const [header, setHeader] = useState<ImportHeaderDTO | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(true)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [actionBusy, setActionBusy] = useState(false)
  const [restartSignal, setRestartSignal] = useState(0)

  // Tabla de detalles: buscador (cualquier columna), paginado y page-size (client-side,
  // los detalles llegan todos en el GET). Mismo patrón que FavoriteDestination/consult.
  const [detailSearch, setDetailSearch] = useState('')
  const [detailPage, setDetailPage] = useState(0)
  const [detailPageSize, setDetailPageSize] = useState(10)

  // Modal de confirmación (cancelar / eliminar detalle)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmTitle, setConfirmTitle] = useState('')
  const pendingAction = useRef<(() => void) | null>(null)

  /**
   * El borrado tiene su propio modal, no el `openConfirm` genérico de arriba.
   * Ese guarda un `() => void` sin parámetros y no valida nada; aquí hace falta
   * capturar un motivo y bloquear el botón hasta que sea válido.
   *
   * `deleteReason` vive acá y no dentro del modal a propósito: al aceptar,
   * `ModalQuestion` hace `setOpen(false)` ANTES de `setAccepts(true)`, así que
   * cuando la acción corre el modal ya se está desmontando.
   */
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteReason, setDeleteReason] = useState('')
  const [deleteLoadingOpen, setDeleteLoadingOpen] = useState(false)
  const [deleteLoadingTitle, setDeleteLoadingTitle] = useState('')
  const [deleteLoadingStatus, setDeleteLoadingStatus] =
    useState<ConfirmModalStatus>(ConfirmModalStatus.PROCESSING)
  /**
   * Envío en curso. Se levanta al confirmar y NO se baja al terminar bien: el
   * borrado es irreversible y la carga ya pasó a "En proceso de anulación", así
   * que un segundo click no tiene nada bueno que hacer. Sólo se baja si la
   * petición falló, para poder reintentar; una recarga lo resetea igual.
   */
  const [deleteSubmitting, setDeleteSubmitting] = useState(false)

  // Modal ver payload (read-only)
  const [payloadOpen, setPayloadOpen] = useState(false)
  const [payloadText, setPayloadText] = useState('')
  /** Identidad de la fila abierta, en la gramática canónica (`detailLabel`). */
  const [payloadLabel, setPayloadLabel] = useState('')

  /**
   * Toolbar (buscador + exports). Es el ancla del scroll cuando se hace click en
   * la identidad de una línea de log: lo que tiene que quedar a la vista es el
   * buscador —ahí se ve el filtro aplicado y ahí se limpia—, no la tabla.
   */
  const toolbarRef = useRef<HTMLDivElement>(null)

  // Info del header: al hacer click se despliega hacia abajo (hover = tooltip).
  const [infoOpen, setInfoOpen] = useState(false)

  // Generando la plantilla Excel con las guías en error (ExcelJS tarda con cargas grandes).
  const [generatingErrorFile, setGeneratingErrorFile] = useState(false)
  /**
   * Ya se bajó el archivo de errores en ESTA visita: se esconde "Descargar guías con
   * error" y en su lugar aparece "Subir archivo corregido".
   *
   * No se persiste a propósito: al recargar vuelve el botón de descargar, que es
   * justamente lo que necesita el operador que perdió o nunca guardó el Excel.
   */
  const [errorFileDownloaded, setErrorFileDownloaded] = useState(false)

  const prevDone = useRef(0)

  // --- Manifiesto de despacho ---
  const [manifestOpen, setManifestOpen] = useState(false)
  const [printOpen, setPrintOpen] = useState(false)
  const [printCode, setPrintCode] = useState('')
  /**
   * El manifiesto se creó pero la marca en Django falló. Banner persistente: el
   * operador necesita el Nº a mano para conciliar con soporte, y tiene que
   * seguir visible después de cerrar el modal.
   */
  const [markError, setMarkError] = useState<string | null>(null)
  /**
   * Marca pendiente: el manifiesto existe en .NET pero Django no quedó marcado.
   *
   * Se persiste en localStorage para sobrevivir al reload y al cierre de la
   * pestaña, y se reconcilia contra el servidor en cada carga del detalle.
   */
  const [pendingMark, setPendingMark] = useState<PendingManifestMark | null>(
    () => getPendingManifestMark(id),
  )
  const [markRetrying, setMarkRetrying] = useState(false)
  /**
   * Aviso de éxito del manifiesto.
   *
   * Vive en el HOST y no en `DispatchCreationFlow` porque el flujo se cierra
   * apenas el manifiesto se crea: si el aviso viviera adentro se desmontaría con
   * él y el operador se quedaría sin el número ni la opción de imprimir.
   */
  const [manifestSuccess, setManifestSuccess] = useState<{
    code: string
    message: string
  } | null>(null)
  /**
   * Destino del foco al abrir el modal.
   *
   * Apunta al encabezado del diálogo, no al buscador: `FormText` declara un prop
   * `ref` en su interfaz pero es un componente función plano, no un
   * `forwardRef`, así que React lo descarta y ese ref nunca llega al `<input>`.
   * Enfocar el encabezado es el patrón estándar de diálogo, mete el foco dentro
   * del modal (que es lo que hace funcionar Escape y la trampa de Tab) y además
   * los lectores de pantalla anuncian el título.
   */
  const manifestFocusRef = useRef<HTMLElement | null>(null)

  // --- Etiquetas / impresión ---
  const [labelOpen, setLabelOpen] = useState(false)
  const [labelBatch, setLabelBatch] = useState(0)
  const [labelFormat, setLabelFormat] = useState<'pdf' | 'pdf-letter'>('pdf')
  /** Filas a imprimir, sea de la cabecera (todas) o de la selección. */
  const [printRows, setPrintRows] = useState<BulkPrintRow[]>([])

  // Documento Bolsillo
  const [pocketOpen, setPocketOpen] = useState(false)
  const [pocketBatch, setPocketBatch] = useState(0)

  // Zebra: cola de grupos, progreso y resultado
  const [zebraBusy, setZebraBusy] = useState(false)
  const [zebraProgress, setZebraProgress] = useState<{
    current: number
    total: number
  } | null>(null)
  /**
   * Grupos pendientes de imprimir y en cuál vamos.
   *
   * Zebra no se puede recorrer como los PDF (donde el operador pasa de página en el
   * visor): cada grupo SE IMPRIME de verdad. Por eso se encola y se avanza recién cuando
   * el operador ve el resultado del grupo anterior y pide el siguiente.
   */
  const [zebraQueue, setZebraQueue] = useState<{
    groups: BulkPrintRow[][]
    index: number
    kind: 'zebra-10x6' | 'zebra-10x10'
  } | null>(null)
  const [zebraResult, setZebraResult] = useState<{
    status: ConfirmModalStatus
    title: string
    /** Hay otro grupo esperando: el modal ofrece imprimirlo. */
    nextGroup?: number
    totalGroups?: number
  } | null>(null)
  /**
   * Cancelación y desmontaje. Refs y no estado: el bucle los lee entre guías y un
   * `useState` le daría el valor congelado del render en el que arrancó.
   */
  const zebraCancelRef = useRef(false)
  /** Espejo en estado del ref de arriba, solo para que el botón se vea deshabilitado. */
  const [zebraCancelling, setZebraCancelling] = useState(false)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  /**
   * Hay una impresión en curso. Guarda de re-entrada: sin esto un doble click abre dos
   * visores o dispara dos bucles Zebra sobre las mismas guías.
   */
  const printBusyRef = useRef(false)

  /** Ya se atendió el `?manifest=1` con el que llega el atajo del listado. */
  const autoOpenedRef = useRef(false)

  /**
   * Ninguna guía de la carga quedó disponible para despacho.
   *
   * Se cierra el modal de creación y queda solo la explicación: crear un manifiesto
   * sin una sola guía de esta carga sería crear OTRO manifiesto, que además quedaría
   * marcado como el de esta carga y taparía el botón para el que sí corresponde.
   */
  const [noUsableGuides, setNoUsableGuides] = useState<{
    notInList: string[]
    withoutPieces: string[]
  } | null>(null)
  const [noUsableResults, setNoUsableResults] = useState<
    ManifestByShipmentDto[]
  >([])
  const [noUsableLoading, setNoUsableLoading] = useState(false)
  const [noUsableError, setNoUsableError] = useState<string | null>(null)

  const handleNoUsableGuides = useCallback(
    (info: { notInList: string[]; withoutPieces: string[] }) => {
      setManifestOpen(false)
      setNoUsableGuides(info)
      if (info.notInList.length === 0) return // nada que buscar: todas sin piezas
      setNoUsableLoading(true)
      setNoUsableError(null)
      getManifestsByShipment(info.notInList)
        .then((res) => {
          if (res.didError) {
            setNoUsableError(res.errorMessage || 'consulta no disponible')
            setNoUsableResults([])
            return
          }
          setNoUsableResults(res.model ?? [])
        })
        .catch((e: any) => {
          setNoUsableError(e?.message || 'consulta no disponible')
          setNoUsableResults([])
        })
        .finally(() => setNoUsableLoading(false))
    },
    [],
  )

  const fetchDetail = useCallback(async () => {
    if (!headerId) return
    const res = await getImport(headerId)
    if (res.didError) {
      setDetailError(messageForCode(res.code, res.errorMessage))
      setLoadingDetail(false)
      return
    }
    setHeader(res.model)
    setDetailError(null)
    setLoadingDetail(false)
  }, [headerId])

  // Carga inicial + recarga tras acciones (restartSignal).
  useEffect(() => {
    setLoadingDetail(true)
    fetchDetail()
  }, [fetchDetail, restartSignal])

  // Al completar: recargar el detalle final. El reintento de errores de servidor (78)
  // es manual (botón Reintentar) o del propio backend; no hay auto-retry del cliente.
  const onComplete = useCallback(
    async (_p: ImportProgressDTO) => {
      if (!headerId) return
      const res = await getImport(headerId)
      if (res.didError || !res.model) return
      setHeader(res.model)
    },
    [headerId],
  )

  /**
   * La carga es de OTRA línea de facturación.
   *
   * La URL es pegable y queda en el historial: el operador abre una carga con
   * una línea, se desloguea y vuelve con otra sin tocar la URL. El backend
   * autoriza por `creation_user`, no por línea, así que lo deja pasar. Acá se
   * corta: sin polling, sin render y de vuelta al listado.
   */
  const denied = useMemo(
    () => !!header && !ownsBulkUpload(header, user.client),
    [header, user.client],
  )

  useEffect(() => {
    if (!denied) return
    // `replace` para que Atrás no devuelva a la URL denegada.
    navigate(LIST_ROUTE, {
      replace: true,
      state: { deniedMessage: BULK_UPLOAD_DENIED_MESSAGE },
    })
  }, [denied, navigate])

  const {
    progress,
    connectionLost,
    notFound,
    refresh: refreshProgress,
  } = useBulkUploadProgress(headerId, {
    onComplete,
    restartSignal,
    enabled: !denied,
  })

  // Recargar detalle cuando avanza el conteo (filas nuevas procesadas).
  useEffect(() => {
    if (!progress) return
    const done = (progress.executed_count ?? 0) + (progress.error_count ?? 0)
    if (done !== prevDone.current) {
      prevDone.current = done
      if (!progress.is_complete) fetchDetail()
    }
  }, [progress, fetchDetail])

  // --- Estado derivado ---
  const statusName = progress?.status_name ?? header?.status_name ?? null
  const statusId = progress?.status_id ?? header?.status_id ?? null
  const meta = headerStatusMeta(statusName, statusId)
  /**
   * Estatus canónico, con el mismo fallback por `status_id` que el badge.
   *
   * Antes estos tres booleanos comparaban el string crudo mientras
   * `headerStatusMeta` sí caía al id: la pantalla decía "Finalizado" y el botón
   * de crear manifiesto no aparecía. Si el badge y un botón no coinciden, uno de
   * los dos miente.
   */
  const canonicalStatus = resolveHeaderStatusName(statusName, statusId)
  const isInProcess = canonicalStatus === ImportHeaderStatusName.InProcess
  const isFinalizedWithErrors =
    canonicalStatus === ImportHeaderStatusName.FinalizedWithErrors
  const isFinalized = canonicalStatus === ImportHeaderStatusName.Finalized

  // Detalle completo del header, sin truncar. Se usa tanto en el tooltip (hover)
  // como en el panel desplegable (click). Devuelve null si aún no hay header.
  const renderHeaderInfo = () => {
    if (!header) return null
    return (
      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 text-sm">
        <dt className="text-gray-400">Estatus</dt>
        <dd className="text-gray-800">{meta.label}</dd>
        <dt className="text-gray-400">Fecha Inicio</dt>
        <dd className="text-gray-800">{formatDateTime(header.start_date)}</dd>
        {header.end_date && (
          <>
            <dt className="text-gray-400">Fecha Fin</dt>
            <dd className="text-gray-800">{formatDateTime(header.end_date)}</dd>
          </>
        )}
        <dt className="text-gray-400">Por</dt>
        <dd className="break-words text-gray-800">
          {header.creation_user_name ?? '–'}
        </dd>
        <dt className="text-gray-400">Línea de facturación</dt>
        <dd className="break-words text-gray-800">{ownerLabel(header)}</dd>
        {hasManifest(header) && (
          <>
            <dt className="text-gray-400">Manifiesto</dt>
            <dd className="break-words text-gray-800">
              {header.manifest_code ?? 'Creado'}
            </dd>
            <dt className="text-gray-400">Fecha del manifiesto</dt>
            <dd className="text-gray-800">
              {formatDateTime(header.manifest_create_date)}
            </dd>
          </>
        )}
      </dl>
    )
  }

  const details = useMemo(() => header?.details ?? [], [header])
  // Estado de los botones derivado de agregar los estatus de los detalles + la cabecera
  // (la spec exige calcularlo desde los detalles, no desde progress.*_count).
  const flags = useMemo(
    () => getBulkActionFlags(details, statusName, statusId),
    [details, statusName, statusId],
  )
  const executedCount = flags.executedCount
  const errorCount = flags.dataErrorCount + flags.serverErrorCount

  /**
   * Cuántas guías reales creó esta carga. Es el número que va en el título del
   * modal de borrado: el operador tiene que poder contrastarlo con lo que cree
   * que está borrando ANTES de confirmar, no leer un aviso genérico que se lee
   * igual para 3 guías que para 400.
   *
   * Se cuenta por `shipment_header_id` y no por estatus del detalle: una línea
   * que ya cambió de estatus igual creó su guía.
   */
  const annulCount = useMemo(
    () => details.filter((d) => !!d.shipment_header_id).length,
    [details],
  )

  /**
   * Los detalles que tenemos en pantalla son posteriores al fin de la anulación.
   *
   * Se levanta una sola vez, cuando el progreso reporta que terminó y la recarga
   * del detalle ya volvió. Hasta entonces no se puede afirmar nada por fila.
   */
  const [detailsAfterAnnulment, setDetailsAfterAnnulment] = useState(false)
  const annulmentSettled = useRef(false)

  useEffect(() => {
    if (!progress?.annulment_done || annulmentSettled.current) return
    annulmentSettled.current = true
    // Recarga explícita: el detalle en memoria es de antes de que la task
    // escribiera los estatus finales.
    fetchDetail().then(() => setDetailsAfterAnnulment(true))
  }, [progress?.annulment_done, fetchDetail])

  /** La carga está borrándose: la anulación corre en background. */
  const isAnnulling = canonicalStatus === ImportHeaderStatusName.Annulling

  /** Cuántas llevan anuladas. Sube solo mientras la task avanza. */
  const annulledSoFar = useMemo(
    () =>
      details.filter(
        (d) =>
          resolveDetailStatusName(d.status_name, d.status_id) ===
          ImportDetailStatusName.Annulled,
      ).length,
    [details],
  )

  /**
   * Las que no se pudieron anular: crearon guía y su detalle quedó en Executed
   * **después** de que la anulación terminó. Ese "después" es lo que hace la
   * distinción posible — durante la corrida, un detalle en Executed es uno al que
   * la task todavía no llegó, y por eso esto depende de `annulment_done`.
   */
  const skippedShipments = useMemo(() => {
    // Ojo con la condición: NO alcanza con que el progreso diga que terminó.
    //
    // `progress` y `details` son dos peticiones distintas que se refrescan por su
    // cuenta. Cruzar la señal fresca ("ya terminé") con la foto vieja del detalle
    // (la línea todavía en Executed) hacía que la pantalla afirmara "no se pudo
    // anular" sobre una guía que un segundo después aparecía anulada. Afirmar algo
    // falso sobre una guía viva es peor que no decir nada.
    //
    // Por eso esto espera a `detailsAfterAnnulment`: los detalles recargados
    // DESPUÉS de que la anulación terminó. Recién ahí una línea en Executed
    // significa de verdad "no se pudo anular" y no "todavía no le toca".
    if (!detailsAfterAnnulment) return []
    return details.filter(
      (d) =>
        !!d.shipment_header_id &&
        resolveDetailStatusName(d.status_name, d.status_id) ===
          ImportDetailStatusName.Executed,
    )
  }, [details, detailsAfterAnnulment])

  /**
   * Catálogos de la plantilla Excel (los mismos que usa la pantalla de creación).
   *
   * Solo se piden si esta carga TIENE errores: sin el `enabled` una carga finalizada
   * limpia dispararía cuatro peticiones para un botón que no se va a mostrar.
   */
  const bulkTemplate = useBulkTemplateArgs({ enabled: errorCount > 0 })

  /**
   * Universo de guías de la carga: las que EXISTEN, con número.
   *
   * No se usa `flags.executedCount` porque ese cuenta filas Executed incluidas
   * las que no tienen `shipment_number`, y una guía sin número no se puede
   * preseleccionar ni imprimir. Si se usara como universo, el aviso "vas a
   * incluir N de M" mentiría.
   */
  const bulkGuides = useMemo(() => executedGuideNumbers(details), [details])

  /**
   * ¿Se puede imprimir? Solo con la carga cerrada.
   *
   * También en "Finalizado con errores": imprimir las guías que SÍ se crearon es inocuo,
   * a diferencia del manifiesto, que se gatilla solo en Finalizado.
   */
  const canPrint = isFinalized || isFinalizedWithErrors

  /**
   * Filas imprimibles, con el servicio y la oficina origen que necesita el Documento
   * Bolsillo. Mismo predicado que `bulkGuides` (hay un test de paridad).
   *
   * Se calcula SOLO cuando la carga ya cerró, y ese `canPrint` no es cosmético: mientras
   * la carga procesa, el polling recarga el detalle cada ~2 s y esto re-parsearía el
   * `payload_json` de hasta 5000 filas en cada ciclo, para alimentar botones que en ese
   * estado ni siquiera se muestran.
   */
  const printableRows = useMemo(
    () => (canPrint ? printableRowsFromDetails(details) : []),
    [canPrint, details],
  )

  /**
   * Reconciliación: si el servidor YA dice que está manifestada, el pendiente
   * dejó de existir.
   *
   * Pasa cuando alguien la marcó desde otra pestaña, cuando el backend se
   * arregló, o cuando el reintento de otra sesión funcionó. Sin esto el banner
   * quedaría para siempre pidiendo reintentar algo que ya está hecho.
   */
  useEffect(() => {
    if (!header || !headerId) return
    if (!hasManifest(header)) return
    clearPendingManifestMark(headerId)
    setPendingMark(null)
    setMarkError(null)
  }, [header, headerId])

  /**
   * ¿La carga es del cliente activo? Misma regla que `denied`, en positivo.
   *
   * Con la guarda de arriba esto es true siempre que la pantalla llegue a
   * renderizar; se conserva porque es lo que leen el botón de manifiesto y la
   * descarga de plantilla, que dependen de que `user.client` sea el dueño
   * (`GetAllMissingCCW` consulta por cliente activo: con otro, la preselección
   * no matchearía nada y la feature parecería andar sin hacer nada).
   *
   * SIGUE SIN SER SEGURIDAD: confirmado (2026-08-04) que
   * `POST /shipment-bulk/{id}/manifest` no evalúa al usuario, igual que el resto
   * de `/shipment-bulk/*`. La autorización real tiene que estar en el backend.
   */
  const ownerMatches = useMemo(
    () => ownsBulkUpload(header, user.client),
    [header, user.client],
  )

  /**
   * Caché del nº de guía aliada por fila, con las dos entradas de las que sale
   * (`payload_json` y `shipment_header_reference`) para saber cuándo revalidar.
   *
   * Existe por el POLLING: `details` sale de `header`, y el efecto del progreso
   * llama a `fetchDetail()` cada vez que avanza el contador (≈cada 2 s mientras
   * la carga procesa). Sin caché, cada uno de esos refetch re-parsea el
   * `payload_json` de TODAS las filas —hasta 5000— para sacar un string que en
   * la enorme mayoría no cambió. La clave incluye el payload, así que una fila
   * corregida (payload reemplazado) sí se vuelve a parsear.
   */
  type PartnerIdCacheEntry = {
    payload: string
    reference: string | null
    partnerId: string | null
  }
  const partnerIdCache = useRef(new Map<string, PartnerIdCacheEntry>())

  /**
   * Índice de filas por id normalizado, con el nº de guía aliada YA resuelto.
   *
   * Es el join de las líneas del log contra sus filas, y el único lugar donde se
   * resuelve el id: hacerlo por línea de log (o por render de celda) repetiría el
   * mismo trabajo decenas de veces.
   */
  type DetailIndexEntry = { detail: ImportDetailDTO; partnerId: string | null }
  const detailIndex = useMemo(() => {
    const previous = partnerIdCache.current
    // Se reconstruye el caché en cada pasada en vez de mutar el anterior: así las
    // filas que el backend borró (re-subir el archivo elimina las pendientes) no
    // quedan acumuladas.
    const next = new Map<string, PartnerIdCacheEntry>()
    const index = new Map<string, DetailIndexEntry>()
    for (const d of details) {
      const key = detailKey(d.shipment_bulk_upload_detail_id)
      const cached = previous.get(key)
      const partnerId =
        cached &&
        cached.payload === d.payload_json &&
        cached.reference === d.shipment_header_reference
          ? cached.partnerId
          : detailPartnerId(d)
      next.set(key, {
        payload: d.payload_json,
        reference: d.shipment_header_reference,
        partnerId,
      })
      index.set(key, { detail: d, partnerId })
    }
    partnerIdCache.current = next
    return index
  }, [details])

  /**
   * Nº de guía aliada de una fila que ya se tiene a mano. Va por el índice (que
   * ya lo calculó) y, si por lo que sea la fila no estuviera indexada, lo calcula
   * — nunca devuelve vacío por un id que no matcheó.
   */
  const rowPartnerId = (d: ImportDetailDTO): string | null =>
    detailIndex.get(detailKey(d.shipment_bulk_upload_detail_id))?.partnerId ??
    detailPartnerId(d)

  // Buscador por cualquier columna (línea, estado, guía, guía aliada —con el
  // fallback al payload de las filas que aún no crearon la guía—, mensaje, fecha).
  const filteredDetails = useMemo(() => {
    const tokens = detailSearch.toLowerCase().split(' ').filter(Boolean)
    if (!tokens.length) return details
    return details.filter((d) => {
      const haystack = [
        d.line_number,
        detailStatusMeta(d.status_name, d.status_id).label,
        d.shipment_number,
        rowPartnerId(d),
        d.error_message,
        formatDateTime(d.executed_date),
      ]
        .map((v) => (v ?? '').toString().toLowerCase())
        .join(' ')
      return tokens.every((t) => haystack.includes(t))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details, detailSearch, detailIndex])

  const paginatedDetails = useMemo(() => {
    const start = detailPage * detailPageSize
    return filteredDetails.slice(start, start + detailPageSize)
  }, [filteredDetails, detailPage, detailPageSize])

  // --- Selección de guías para imprimir ---
  const {
    selectedPrintRows,
    printableById,
    filteredPrintableRows,
    allFilteredSelected,
    isSelected,
    toggleRow,
    toggleAllFiltered,
    clear: clearSelection,
  } = useBulkPrintSelection({
    printableRows,
    filteredIds: useMemo(
      () => filteredDetails.map((d) => d.shipment_bulk_upload_detail_id),
      [filteredDetails],
    ),
    paginatedIds: useMemo(
      () => paginatedDetails.map((d) => d.shipment_bulk_upload_detail_id),
      [paginatedDetails],
    ),
    viewKey: `${detailSearch}|${detailPage}|${detailPageSize}`,
  })

  // Export (PDF/Excel/TXT) del conjunto FILTRADO completo (no solo la página visible).
  const DETAIL_EXPORT_COLUMNS = [
    'Línea',
    'Estado',
    'Guía',
    'Guía aliada',
    'Mensaje',
    'Procesado',
  ]
  /**
   * Columnas del export que pueden traer texto del archivo que subió el operador:
   * "Guía aliada" (sale del Excel) y "Mensaje" (el backend cita adentro valores del
   * operador, ej. "nombres de cliente no son válidos: =CMD()"). Son las únicas que
   * pasan por `sanitizeForExcel` — el resto son número de línea, etiqueta de estado,
   * nº de guía TEALCA y fecha, donde la comilla del sanitizador sería basura visible.
   */
  const DETAIL_EXPORT_UNSAFE_COLUMNS = [3, 4]
  const buildDetailExportRows = (): (string | number)[][] =>
    filteredDetails.map((d) => [
      d.line_number,
      detailStatusMeta(d.status_name, d.status_id).label,
      d.shipment_number ?? '',
      rowPartnerId(d) ?? '',
      d.error_message ?? '',
      formatDateTime(d.executed_date),
    ])

  // --- Acciones ---
  const openConfirm = (title: string, action: () => void) => {
    setConfirmTitle(title)
    pendingAction.current = action
    setConfirmOpen(true)
  }
  const onAccepts: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
    if (value === true && pendingAction.current) pendingAction.current()
    pendingAction.current = null
  }

  const runAction = async (
    fn: () => Promise<{
      didError: boolean
      code: string
      errorMessage: string
    }>,
    okMsg: string,
    after?: () => void,
  ) => {
    setActionBusy(true)
    const r = await fn()
    setActionBusy(false)
    if (r.didError) {
      alertService.error(
        'No se pudo completar',
        messageForCode(r.code, r.errorMessage),
      )
      return
    }
    alertService.success(okMsg)
    if (after) after()
    else setRestartSignal((s) => s + 1)
  }

  const doCancel = () =>
    runAction(() => cancelImport(headerId!), 'Carga masiva cancelada')

  /**
   * Borrado con motivo. No usa `runAction` por dos razones.
   *
   * Una: `runAction` reporta por `alertService`, y el componente `<Alert />` no
   * está montado en `App.tsx` — esos avisos se publican al vacío. Para una acción
   * que anula guías de un cliente el operador no puede quedarse sin señal, así que
   * el feedback va por `ModalLoading`.
   *
   * Dos: ya NO navegamos al listado. El endpoint responde 202 y la anulación sigue
   * corriendo atrás; si sacamos al operador de esta pantalla, se pierde el progreso
   * y el reporte de las guías que no se pudieron anular, que es el resultado que
   * más le importa.
   */
  const doDelete = async (reason: string) => {
    if (deleteSubmitting) return
    setDeleteSubmitting(true)
    setDeleteLoadingStatus(ConfirmModalStatus.PROCESSING)
    setDeleteLoadingTitle(
      'Procesando solicitud de eliminación de carga masiva...',
    )
    setDeleteLoadingOpen(true)

    const r = await deleteImport(headerId!, reason)

    if (r.didError) {
      // Sólo aquí se libera: la petición no llegó a cambiar nada, el operador
      // tiene que poder corregir el motivo y reintentar.
      setDeleteSubmitting(false)
      setDeleteLoadingStatus(ConfirmModalStatus.ERROR)
      setDeleteLoadingTitle(messageForCode(r.code, r.errorMessage))
      return
    }

    const queued = r.model?.shipments_to_annul ?? 0
    setDeleteLoadingStatus(ConfirmModalStatus.SUCCESS)
    // NO decir "eliminada" ni "se anularon N guías": el endpoint respondió 202, la
    // cabecera está en "En proceso de anulación" y todavía no se anuló ni una. Un
    // mensaje en pasado sobre un trabajo que no empezó es una mentira, y encima la
    // más cara de todas porque el operador se va tranquilo.
    setDeleteLoadingTitle(
      queued > 0
        ? `Procesando la anulación de ${queued} guía${queued === 1 ? '' : 's'}.
Puedes monitorear el proceso en la pantalla.`
        : 'Procesando la solicitud de eliminación de la carga.',
    )
    setDeleteReason('')

    // Refrescar YA las dos fuentes, sin esperar al polling.
    //
    // El badge, los botones y el bloque de progreso leen de `progress`, no de
    // `header`: bumpear `restartSignal` recarga el detalle pero deja el estatus
    // viejo en pantalla hasta el siguiente tick, y la acción se siente como si no
    // hubiera hecho nada.
    refreshProgress()
    setRestartSignal((s) => s + 1)
  }

  const doRetry = () =>
    runAction(
      () => executeImport(headerId!, true),
      'Reintentando errores',
      () => setRestartSignal((s) => s + 1),
    )

  const doDeleteDetail = (detailId: string) =>
    runAction(
      () => deleteImportDetail(headerId!, detailId),
      'Detalle eliminado',
    )

  const doDownload = async () => {
    if (!headerId) return
    setActionBusy(true)
    const r = await getImportFile(headerId)
    setActionBusy(false)
    if (r.didError || !r.model?.url) {
      alertService.error(
        'No se pudo descargar',
        messageForCode(r.code, r.errorMessage),
      )
      return
    }
    window.open(r.model.url, '_blank', 'noopener')
  }

  /**
   * Descarga las guías con error en la PLANTILLA de carga masiva, ya llenas y con el
   * motivo del error en la última columna.
   *
   * Es el camino alterno a "Corregir errores": en vez de editar guía por guía en el
   * editor web, el operador arregla el archivo en Excel —donde ya sabe trabajar y donde
   * puede usar buscar/reemplazar sobre cientos de filas— y lo vuelve a subir como una
   * carga nueva.
   */
  const doDownloadErrorTemplate = async () => {
    if (!header || generatingErrorFile) return

    /**
     * El botón NO se deshabilita mientras faltan catálogos.
     *
     * `SecondaryButton` traduce `disabled` a `pointer-events-none`, así que un botón
     * deshabilitado no dispara NADA al hacer click: el operador ve que "no pasa nada" y no
     * tiene forma de saber por qué. Se prefiere dejarlo activo y decir en un aviso qué
     * falta —que además se resuelve solo en un par de segundos—.
     */
    if (!bulkTemplate.ready) {
      alertService.info(
        'Todavía no se puede generar el archivo',
        `${bulkTemplate.notReadyReason} La plantilla necesita las tiendas, ciudades y servicios de tu contrato para armar sus listas desplegables. Probá de nuevo en unos segundos.`,
      )
      return
    }

    if (!ownerMatches) {
      alertService.warn(
        'Esta carga masiva es de otro cliente',
        'El archivo se arma con las tiendas y servicios del cliente activo. Cambiá de cliente en el encabezado para bajarlo con los datos correctos.',
      )
      return
    }

    // Solo errores de DATO (75/77) — el mismo filtro que el editor `/fix`.
    //
    // Los de servidor (78) quedan afuera a propósito: se resuelven con "Reintentar", no
    // cambiando datos, y el `PUT /update` que recibe el archivo corregido solo acepta
    // 75/77. Incluirlos daría un archivo con filas que el operador corrige y que después
    // no se pueden volver a subir. Así el conjunto es el mismo de punta a punta.
    const errorDetails = details.filter((d) => {
      const s = resolveDetailStatusName(d.status_name, d.status_id)
      return (
        s === ImportDetailStatusName.Error ||
        s === ImportDetailStatusName.ErrorProcessing
      )
    })
    if (errorDetails.length === 0) {
      alertService.info('Esta carga masiva no tiene guías con error.')
      return
    }

    setGeneratingErrorFile(true)
    try {
      // Un payload_json corrupto de UNA fila no puede tumbar la descarga entera: se
      // descarta esa guía y se avisa cuántas quedaron afuera, que es información que el
      // operador necesita para no creer que el archivo está completo.
      const skipped: number[] = []
      const inputs = errorDetails.flatMap((d) => {
        try {
          const group = payloadJsonToShipmentGroup(d.payload_json, {
            identificationTypes: bulkTemplate.catalogs.identificationTypes,
            isCOD: bulkTemplate.isCODForReview,
          })
          return [
            {
              group,
              /**
               * Nº de guía aliada. Es la única columna cuya pérdida no deja rastro: el
               * parser descarta enteras las filas que no la traen.
               *
               * `detailPartnerId` es el MISMO resolutor que usan la tabla, el buscador
               * y el log: detalle primero, con fallback al `partner_shipment_id` del
               * payload, porque `shipment_header_reference` viene vacío en las filas que
               * fallaron antes de crear la guía — o sea, justo en las que este archivo
               * existe para recuperar.
               */
              tracking: detailPartnerId(d) ?? '',
              errorReason: d.error_message || '',
              // Columna oculta: es con esto que la fila reencuentra su línea al volver a
              // subir el archivo, aunque el operador le cambie el nº de guía aliada.
              lineId: d.shipment_bulk_upload_detail_id,
            },
          ]
        } catch {
          skipped.push(d.line_number)
          return []
        }
      })

      if (inputs.length === 0) {
        alertService.error(
          'No se pudo generar el archivo',
          'No se pudo reconstruir ninguna de las guías con error de esta carga. Corregilas desde "Corregir errores" o reportalo a soporte.',
        )
        return
      }

      // Guías que se quedaron SIN nº de guía aliada (ni en el detalle ni en el payload).
      // Se incluyen igual, con su fila en blanco en la columna A: excluirlas dejaría al
      // operador sin saber que existen, y así al menos puede escribir el número. Se avisa
      // porque, tal cual salen, el sistema las descarta al re-subir el archivo.
      const untracked = inputs.filter((i) => !i.tracking.trim())

      // Guías con error que COMPARTEN nº de guía aliada (causa frecuente del error): al
      // re-subir el archivo el sistema las fusiona en una sola, porque agrupa por ese
      // número. Se avisa antes de que el operador lo descubra con una guía de menos.
      const seen = new Set<string>()
      const duplicated = new Set<string>()
      inputs.forEach((i) => {
        const key = i.tracking.trim().toUpperCase()
        if (!key) return
        if (seen.has(key)) duplicated.add(i.tracking.trim())
        else seen.add(key)
      })

      const rows = shipmentGroupsToTemplateRows(inputs, {
        identificationTypes: bulkTemplate.catalogs.identificationTypes,
        citiesType: bulkTemplate.catalogs.citiesType,
        businessUnits: bulkTemplate.catalogs.businessUnitOptions,
        pieceCategories: bulkTemplate.catalogs.pieceCategories,
        originBusinessUnits: bulkTemplate.catalogs.originBusinessUnits,
        serviceOptions: bulkTemplate.catalogs.serviceOptions,
      })

      await exportFormatoShipmentXLSX_V2WithCoords(
        ...bulkTemplate.templateArgs,
        {
          rows,
          extraColumns: BULK_CORRECTION_EXTRA_COLUMNS,
          fileNamePrefix: `GuiasConError_${header.bulk_upload_number}`,
        },
      )

      // Habilita "Subir archivo corregido" en lugar del botón de descarga. Es estado de
      // esta visita nada más: al recargar vuelve a verse el de descargar, que es también
      // la salida para el que perdió el archivo.
      setErrorFileDownloaded(true)

      if (skipped.length > 0)
        alertService.warn(
          'Algunas guías quedaron fuera del archivo',
          `No se pudieron reconstruir ${skipped.length} guía(s) (línea${
            skipped.length > 1 ? 's' : ''
          } ${skipped.slice(0, 10).join(', ')}${skipped.length > 10 ? '…' : ''}). Corregilas desde "Corregir errores".`,
          { autoClose: false },
        )
      if (untracked.length > 0)
        alertService.warn(
          'Guías sin número de guía aliada',
          `${untracked.length} guía(s) quedaron con la columna "Número de guía aliada" vacía. Completala en el Excel antes de subir el archivo: sin ese número el sistema descarta la fila.`,
          { autoClose: false },
        )
      if (duplicated.size > 0)
        alertService.warn(
          'Números de guía aliada repetidos',
          `Estas guías comparten número: ${Array.from(duplicated).slice(0, 10).join(', ')}. Al volver a cargar el archivo se van a unir en una sola guía — cambiales el número antes de subirlo.`,
          { autoClose: false },
        )
      if (!flags.isHeaderClosed)
        alertService.info(
          'Evitá cargar dos veces las mismas guías',
          'Esta carga todavía admite "Reintentar" y "Corregir errores". Si además usás ese camino sobre las mismas guías, podrías terminar con guías duplicadas.',
        )
    } catch (e: any) {
      alertService.error(
        'No se pudo generar el archivo',
        e?.message || 'Ocurrió un error inesperado al armar el Excel.',
      )
    } finally {
      setGeneratingErrorFile(false)
    }
  }

  /**
   * Atajo del listado: `?manifest=1` abre el modal apenas hay datos suficientes.
   *
   * Se espera a tener header y guías porque el modal necesita la preselección; y
   * se limpia el parámetro de la URL para que un F5 o un "atrás" no lo reabran
   * solo. Una vez por visita, con guarda de ref.
   */
  useEffect(() => {
    if (autoOpenedRef.current) return
    if (searchParams.get('manifest') !== '1') return
    if (!header || !isFinalized || hasManifest(header)) return
    if (bulkGuides.length === 0 || !ownerMatches) return

    autoOpenedRef.current = true
    setManifestOpen(true)
    searchParams.delete('manifest')
    setSearchParams(searchParams, { replace: true })
  }, [
    searchParams,
    setSearchParams,
    header,
    isFinalized,
    bulkGuides.length,
    ownerMatches,
  ])

  /**
   * La carga terminó MIENTRAS el operador la miraba → se le ofrece crear el
   * manifiesto ahí mismo.
   *
   * Solo en la TRANSICIÓN. `prevCanonicalStatus` arranca en null y la primera
   * lectura solo lo siembra: entrar a una carga que ya estaba finalizada no
   * dispara nada. Sin esa guarda, cada visita a una carga vieja abriría un modal
   * que el operador no pidió.
   *
   * Se abre la confirmación, no el modal grande: es una interrupción, y el
   * operador tiene que poder decir que no.
   */
  /**
   * Se observó a esta carga en un estado que NO era Finalizado. Es lo que
   * distingue "terminó mientras la mirabas" de "entraste y ya estaba lista".
   */
  const sawNonFinalizedRef = useRef(false)
  /** Terminó mientras el operador miraba, y todavía no se le preguntó. */
  const pendingAskRef = useRef(false)
  const autoAskedOnFinishRef = useRef(false)

  // 1) DETECTAR.
  //
  // NO se compara "estatus anterior contra actual". La primera versión lo hacía y no
  // disparaba nunca: mientras la carga corre, el header reporta estatus que el mapa
  // de CABECERA no cubre ('En ejecucion' es un estatus de DETALLE, id 79, fuera del
  // rango 68–72), así que `canonicalStatus` es null y no había estatus previo que
  // comparar. Mismo choque nombre/id que ya rompió el botón de crear manifiesto.
  //
  // Lo que se recuerda es más simple y no depende de que el estatus intermedio esté
  // mapeado: ¿se vio alguna vez esta carga sin finalizar?
  useEffect(() => {
    // Sin ningún dato de estatus todavía no es "no finalizada", es "no sé".
    if (statusName == null && statusId == null) return

    if (canonicalStatus !== ImportHeaderStatusName.Finalized) {
      sawNonFinalizedRef.current = true
      return
    }
    // Finalizado. Solo se ofrece si antes se la vio sin finalizar: entrar a una
    // carga que ya estaba lista no abre ningún modal que el operador no pidió.
    if (sawNonFinalizedRef.current && !autoAskedOnFinishRef.current) {
      pendingAskRef.current = true
    }
  }, [canonicalStatus, statusName, statusId])

  // 2) CONSUMIR, cuando se pueda. Separado a propósito: al pasar a Finalizado el
  //    detalle se recarga en paralelo, así que en ese instante `bulkGuides` casi
  //    siempre está vacío todavía. Resuelto en un solo efecto, la transición se
  //    perdía justo en el caso normal.
  useEffect(() => {
    if (!pendingAskRef.current || autoAskedOnFinishRef.current) return
    // Mismas condiciones que el botón: sin guías o ya manifestada no hay nada que
    // ofrecer. Y con otro modal abierto se espera, en vez de pisar lo que el
    // operador está haciendo.
    if (!header || hasManifest(header)) return
    if (bulkGuides.length === 0 || !ownerMatches) return
    if (confirmOpen || manifestOpen || labelOpen) return

    autoAskedOnFinishRef.current = true
    pendingAskRef.current = false
    askCreateManifest()
    // `askCreateManifest` se redefine en cada render (cierra sobre `header`); meterlo
    // en deps re-dispararía el efecto sin aportar nada. La guarda de ref es la que manda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    header,
    bulkGuides.length,
    ownerMatches,
    confirmOpen,
    manifestOpen,
    labelOpen,
  ])

  // --- Impresión ---

  /**
   * Tamaño de grupo vigente. Arranca en el tope y baja solo si el servicio se queda sin
   * tiempo. No se restaura al valor alto en la misma visita: si el backend viene lento,
   * volver a 50 solo haría que el operador se coma otro timeout de 30 s. Se restablece
   * al recargar la página, que es lo que dice el mensaje final.
   */
  const [batchSize, setBatchSize] = useState(PRINT_BATCH_LIMIT)

  /** Mismo reparto para los tres visores: PDF 10x10, PDF Carta y Documento Bolsillo. */
  const printGroups = useMemo(
    () => chunkPrintRows(printRows, batchSize),
    [printRows, batchSize],
  )

  /**
   * El visor avisó que se quedó sin tiempo. Se vuelve a partir la selección más chica y
   * se reinicia en el primer grupo: los índices viejos apuntaban a otro reparto.
   *
   * Devuelve `false` cuando ya estaba en el piso — ahí no queda nada que achicar y el
   * visor pasa a mostrar el mensaje de escalar.
   */
  const handleLabelTimeout = (): boolean => {
    const next = degradeBatchSize(batchSize, PRINT_BATCH_MIN)
    if (next === null) return false
    setBatchSize(next)
    setLabelBatch(0)
    setPocketBatch(0)
    return true
  }
  const printGroupCount = Math.max(1, printGroups.length)
  const labelSlice = printGroups[labelBatch] ?? []
  const pocketSlice = printGroups[pocketBatch] ?? []

  /**
   * Corre la tanda Zebra y deja el resultado a la vista.
   *
   * Todo lo que hace de más respecto del bucle de consulta existe por un motivo concreto:
   * consulta ignora el resultado de cada impresión (`PrintLabel` devuelve `false` en vez
   * de lanzar), así que 12 fallos de 40 terminan mostrando "Impresión completada".
   */
  const runZebraGroup = async (
    groups: BulkPrintRow[][],
    index: number,
    kind: 'zebra-10x6' | 'zebra-10x10',
  ) => {
    const rows = groups[index]
    if (!rows?.length) return

    const formatType = ZEBRA_FORMAT_TYPE[kind]
    zebraCancelRef.current = false
    setZebraCancelling(false)
    setZebraQueue({ groups, index, kind })
    setZebraBusy(true)
    setZebraProgress({ current: 0, total: rows.length })

    const result = await runZebraPrint(rows, {
      fetchLabel: (shipmentNumber) =>
        getShipmentZebraLabel(shipmentNumber, formatType),
      sendToPrinter: (stringLabel, format) =>
        sendLabelToPrinter(stringLabel, format),
      isCancelled: () => zebraCancelRef.current,
      isMounted: () => mountedRef.current,
      onProgress: (i, total) => {
        if (!mountedRef.current) return
        setZebraProgress({ current: i + 1, total })
      },
    })

    printBusyRef.current = false
    // Si el operador se fue de la página, no se toca más estado: el bucle ya paró.
    if (!mountedRef.current) return

    setZebraBusy(false)
    setZebraProgress(null)

    /**
     * Solo se ofrece el grupo siguiente si este terminó de correr.
     *
     * Si el operador canceló, o si el agente de impresión está caído, encadenar el
     * siguiente grupo sería seguir haciendo justo lo que se acaba de frenar.
     */
    const hasNext = index + 1 < groups.length && !result.aborted

    setZebraResult({
      ...buildZebraResult(result, {
        group: index + 1,
        totalGroups: groups.length,
      }),
      ...(hasNext ? { nextGroup: index + 1, totalGroups: groups.length } : {}),
    })
  }

  const startPrint = (kind: BulkPrintKind, rows: BulkPrintRow[]) => {
    // Guarda de re-entrada: un doble click no abre dos visores ni dispara dos bucles.
    if (printBusyRef.current) return
    if (rows.length === 0) return

    // `printBusyRef` se marca cuando el visor ABRE, no acá: si se marcara antes de la
    // confirmación y el operador dijera que no, quedaría en true para siempre y
    // bloquearía toda impresión posterior.
    const openLabels = () => {
      printBusyRef.current = true
      setLabelOpen(true)
    }
    const openPocket = () => {
      printBusyRef.current = true
      setPocketOpen(true)
    }

    const groups = chunkPrintRows(rows, batchSize)

    if (kind === 'pdf' || kind === 'pdf-letter') {
      setPrintRows(rows)
      setLabelFormat(kind)
      setLabelBatch(0)
      if (groups.length > 1) {
        // El reparto se dice ANTES de apretar, no después de un timeout.
        openConfirm(
          `Está a punto de generar ${rows.length} etiquetas. El proceso se dividirá en ${groups.length} grupos de hasta ${batchSize} guías que podrá consultar en la vista previa. ¿Deseas continuar?`,
          openLabels,
        )
        return
      }
      openLabels()
      return
    }

    if (kind === 'pocket') {
      setPrintRows(rows)
      setPocketBatch(0)
      if (groups.length > 1) {
        openConfirm(
          `Está a punto de generar ${rows.length} documentos de bolsillo. El proceso se dividirá en ${groups.length} grupos de hasta ${batchSize} guías que podrá consultar en la vista previa. ¿Deseas continuar?`,
          openPocket,
        )
        return
      }
      openPocket()
      return
    }

    // Zebra: los grupos NO se recorren en un visor, se imprimen de verdad. Se arranca
    // por el primero y el siguiente se ofrece recién con el resultado del anterior a la
    // vista.
    const zebraMessage =
      groups.length > 1
        ? `Está a punto de imprimir ${rows.length} etiquetas Zebra. El proceso se dividirá en ${groups.length} grupos de hasta ${batchSize} guías: se imprimirá un grupo por vez y al terminar cada uno podrá continuar con el siguiente. ¿Deseas continuar?`
        : `Está a punto de imprimir ${rows.length} etiqueta(s) Zebra. Se envían de a una a la impresora, así que puede tardar. ¿Deseas continuar?`

    openConfirm(zebraMessage, () => {
      printBusyRef.current = true
      void runZebraGroup(groups, 0, kind)
    })
  }

  /** Ítems del menú de impresión. Se arman una vez y los usan cabecera y barra. */
  const printMenuItems = (rows: BulkPrintRow[]) => [
    {
      items: [
        {
          label: 'Documento Bolsillo',
          icon: PrinterIcon,
          onClick: () => startPrint('pocket', rows),
        },
        {
          label: 'Etiqueta PDF 10x10',
          icon: PrinterIcon,
          onClick: () => startPrint('pdf', rows),
        },
        {
          label: 'Etiqueta PDF Carta',
          icon: PrinterIcon,
          title:
            'Se imprimen hasta 4 etiquetas por hoja carta para guías multipiezas',
          onClick: () => startPrint('pdf-letter', rows),
        },
        {
          label: 'Etiqueta Zebra 10x6',
          icon: PrinterIcon,
          onClick: () => startPrint('zebra-10x6', rows),
        },
        {
          label: 'Etiqueta Zebra 10x10',
          icon: PrinterIcon,
          onClick: () => startPrint('zebra-10x10', rows),
        },
      ],
    },
  ]

  /**
   * Reintenta SOLO la marca en Django. El manifiesto ya existe: acá no se crea
   * nada, así que no hay riesgo de duplicarlo.
   */
  const retryMarkManifest = async () => {
    if (!headerId || !pendingMark) return

    setMarkRetrying(true)
    const res = await createBulkUploadManifest(headerId, pendingMark.id)
    setMarkRetrying(false)

    if (res.didError) {
      const msg = messageForCode(res.code, res.errorMessage)
      savePendingManifestMark(headerId, {
        code: pendingMark.code,
        id: pendingMark.id,
        lastError: msg,
      })
      setPendingMark(getPendingManifestMark(headerId))
      setMarkError(msg)
      return
    }

    clearPendingManifestMark(headerId)
    setPendingMark(null)
    setMarkError(null)
    setRestartSignal((s) => s + 1)
  }

  const askCreateManifest = () =>
    openConfirm(
      `¿Deseas crear un manifiesto basado en esta carga masiva? Se preseleccionarán las ${bulkGuides.length} guías de la carga ${header?.bulk_upload_number ?? ''}.`,
      () => {
        setMarkError(null)
        setManifestOpen(true)
      },
    )

  /**
   * Fase 2 del guardado: el manifiesto YA existe en el API .NET.
   *
   * Un fallo acá no puede presentarse como fallo de creación. Se devuelve el
   * resultado al flujo (que lo anexa a su modal de éxito sin salir de PRINT) y
   * además se deja un banner persistente en la página, que sobrevive al cierre
   * del modal.
   */
  const handleManifestCreated = async (manifest: {
    code: string
    id?: string | null
    rejected: DispatchGuideDto[]
  }) => {
    // Las guías sin piezas no viajan: se dice ahora, no cuando el paquete no
    // llegue.
    const avisoPiezas = manifest.rejected.length
      ? `\n⚠ ${manifest.rejected.length} guía(s) sin piezas quedaron fuera: ${manifest.rejected
          .map((x) => x.numGuia)
          .join(', ')}`
      : ''

    if (!headerId) {
      setManifestSuccess({
        code: manifest.code,
        message: `¡Se registro el despacho Nº ${manifest.code} con exito!${avisoPiezas}`,
      })
      return { ok: true }
    }

    /**
     * Sin GUID no hay vínculo posible: el endpoint recibe `manifest_id` y es lo
     * único que manda. Tampoco tiene sentido dejar un pendiente reintentable —
     * el reintento fallaría igual. Se avisa y se manda a soporte con el número,
     * que es lo único accionable.
     */
    if (!manifest.id) {
      setMarkError(
        'El API de despacho no devolvió el identificador del manifiesto, así que no se pudo vincular con esta carga masiva.',
      )
      setManifestSuccess({
        code: manifest.code,
        message: `¡Se registro el despacho Nº ${manifest.code} con exito!${avisoPiezas}\n⚠ No se pudo vincular con la carga masiva. Reportalo a soporte con ese número.`,
      })
      return {
        ok: false,
        message: 'No se pudo vincular el manifiesto con la carga masiva.',
      }
    }

    const res = await createBulkUploadManifest(headerId, manifest.id)

    if (res.didError) {
      const msg = messageForCode(res.code, res.errorMessage)
      // Se persiste ANTES de mostrar nada: si el operador cierra la pestaña de
      // golpe, el pendiente sigue ahí cuando vuelva.
      savePendingManifestMark(headerId, {
        code: manifest.code,
        id: manifest.id ?? null,
        lastError: msg,
      })
      setPendingMark(getPendingManifestMark(headerId))
      setMarkError(msg)
      setManifestSuccess({
        code: manifest.code,
        message: `¡Se registro el despacho Nº ${manifest.code} con exito!${avisoPiezas}\n⚠ La carga masiva no quedó marcada como manifestada.`,
      })
      return {
        ok: false,
        message: 'La carga masiva no quedó marcada como manifestada.',
      }
    }

    setManifestSuccess({
      code: manifest.code,
      message: `¡Se registro el despacho Nº ${manifest.code} con exito!${avisoPiezas}`,
    })
    setRestartSignal((s) => s + 1)
    return { ok: true }
  }

  const openPayload = (d: ImportDetailDTO) => {
    setPayloadText(prettyJson(d.payload_json))
    setPayloadLabel(detailLabel(d, rowPartnerId(d)))
    setPayloadOpen(true)
  }

  // --- Render ---
  const importationNumber = header?.bulk_upload_number ?? 'Carga masiva'

  // Redirigiendo al listado (efecto de `denied`): no pintar ni un frame con
  // datos de otra línea de facturación.
  if (denied) return null

  if (notFound) {
    return (
      <MainPage>
        <PageTitle
          title="Carga masiva"
          back
          onBack={() => navigate(LIST_ROUTE)}
        />
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          La carga masiva no existe o fue eliminada.
        </div>
      </MainPage>
    )
  }

  return (
    <MainPage>
      <PageTitle
        title={importationNumber}
        back
        onBack={() => navigate(LIST_ROUTE)}
      />

      {detailError && !header && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {detailError}
        </div>
      )}

      {loadingDetail && !header ? (
        <div className="animate-pulse space-y-4">
          <div className="h-24 rounded-lg bg-gray-100" />
          <div className="h-12 rounded-lg bg-gray-100" />
          <div className="h-64 rounded-lg bg-gray-100" />
        </div>
      ) : (
        header && (
          <div className="space-y-5">
            {/* Header card */}
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                {/* Grupo de info: hover = tooltip flotante, click = panel desplegable */}
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={infoOpen}
                  onClick={() => setInfoOpen((v) => !v)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setInfoOpen((v) => !v)
                    }
                  }}
                  className="relative flex min-w-0 cursor-pointer items-center gap-3 rounded-md py-0.5 pr-6 hover:bg-gray-50/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-main-400"
                >
                  <span
                    className={`shrink-0 whitespace-nowrap rounded-full px-3 py-0.5 text-xs font-semibold ${meta.badgeClass}`}
                  >
                    {meta.label}
                  </span>
                  <span className="max-w-[50rem] truncate whitespace-nowrap text-sm text-gray-500">
                    Fecha Inicio {formatDateTime(header.start_date)}
                    {header.end_date
                      ? ` · Fecha Fin ${formatDateTime(header.end_date)}`
                      : ''}
                  </span>
                  <span className="max-w-[12rem] truncate text-sm text-gray-500">
                    Por {header.creation_user_name}
                  </span>
                  <span className="max-w-[45rem] truncate text-sm text-gray-500">
                    Línea de facturación {ownerLabel(header)}
                  </span>
                  <ChevronDownIcon
                    className={`absolute right-1 h-4 w-4 shrink-0 text-gray-400 transition-transform ${
                      infoOpen ? 'rotate-180' : ''
                    }`}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {/* Reintentar / Corregir errores solo sobre una cabecera abierta
                      y con la carga YA PROCESADA al 100%.

                      Lo de "procesada" no es cosmético: con 100 guías, 10
                      procesadas y 3 con error, los botones aparecían y mandaban
                      al operador a corregir 3 mientras las otras 90 seguían
                      generando errores nuevos. La corrección se hace sobre la
                      carga entera, así que hasta que no termina no se ofrece —
                      la misma regla que ya aplicaba "Descargar guías con error".

                      En Finalizado, Cancelado o Eliminado los detalles siguen
                      teniendo sus errores, pero el backend ya no acepta cambios:
                      ahí la única acción que queda es descargar el archivo. */}
                  {errorCount > 0 &&
                    flags.isFullyProcessed &&
                    !flags.isHeaderClosed && (
                      <SecondaryButton
                        onClick={doRetry}
                        disabled={!flags.canRetry || actionBusy}
                        className="h-9 text-xs"
                      >
                        <ArrowPathIcon className="mr-1 h-4 w-4" /> Reintentar
                      </SecondaryButton>
                    )}
                  {errorCount > 0 &&
                    flags.isFullyProcessed &&
                    !flags.isHeaderClosed && (
                      <SecondaryButton
                        onClick={() =>
                          navigate(`/shipment/bulk-upload/${headerId}/fix`)
                        }
                        disabled={!flags.canEditErrors || actionBusy}
                        className="h-9 text-xs"
                      >
                        <PencilSquareIcon className="mr-1 h-4 w-4" /> Corregir
                        errores
                      </SecondaryButton>
                    )}
                  {/* Camino alterno a "Corregir errores": bajar las guías que
                      fallaron en la plantilla de carga masiva, arreglarlas en
                      Excel y volver a subir ESE MISMO archivo sobre esta carga.

                      Los dos botones se turnan: apenas se baja el archivo, el de
                      descargar deja lugar al de subir. Al recargar la página se
                      vuelve al de descargar (el estado no se persiste), que es la
                      salida para quien perdió el Excel.

                      Solo con la cabecera en "Finalizado con errores": es el
                      único estado en el que la carga ya terminó y dejó guías con
                      error de dato para corregir. En proceso los errores todavía
                      pueden cambiar (hay líneas ejecutándose), y en Finalizado /
                      Cancelado / Eliminado no hay nada que bajar para corregir. */}
                  {isFinalizedWithErrors &&
                    flags.dataErrorCount > 0 &&
                    !errorFileDownloaded && (
                      <SecondaryButton
                        onClick={() => void doDownloadErrorTemplate()}
                        // Solo se deshabilita mientras el archivo se está generando.
                        // "Faltan catálogos" y "es de otro cliente" NO deshabilitan: el
                        // `disabled` de SecondaryButton aplica `pointer-events-none`, así
                        // que el click no dispararía nada y el operador vería que el botón
                        // no hace nada, sin ninguna explicación. El handler lo dice.
                        disabled={actionBusy || generatingErrorFile}
                        title={
                          !ownerMatches
                            ? 'Esta carga masiva pertenece a otro cliente. Cambiá de cliente en el encabezado para bajar el archivo con sus tiendas y servicios.'
                            : (bulkTemplate.notReadyReason ??
                              `Descarga las ${flags.dataErrorCount} guía(s) con error de datos en la plantilla de carga masiva, con el motivo de cada una. Corregilas en Excel y volvé a subir el archivo sobre esta misma carga.`)
                        }
                        className="h-9 text-xs"
                      >
                        {generatingErrorFile ? (
                          <ArrowPathIcon className="mr-1 h-4 w-4 animate-spin" />
                        ) : (
                          <DocumentArrowDownIcon className="mr-1 h-4 w-4" />
                        )}
                        Descargar guías con error
                      </SecondaryButton>
                    )}
                  {flags.dataErrorCount > 0 && errorFileDownloaded && (
                    <SecondaryButton
                      onClick={() => {
                        // La cabecera cerrada NO deshabilita el botón (quedaría mudo):
                        // se explica y se ofrece la alternativa real.
                        if (flags.isHeaderClosed) {
                          alertService.warn(
                            'Esta carga ya no admite correcciones',
                            'Está finalizada o cancelada, así que el sistema no puede actualizar sus guías. Puedes subir el archivo corregido como una carga masiva nueva desde el menú de Carga masiva.',
                          )
                          return
                        }
                        navigate(`/shipment/bulk-upload/${headerId}/fix/upload`)
                      }}
                      disabled={actionBusy}
                      title={
                        flags.isHeaderClosed
                          ? 'Esta carga ya está cerrada y no admite correcciones.'
                          : 'Subí el Excel que acabás de corregir. Se van a actualizar estas mismas guías, sin agregar ninguna nueva.'
                      }
                      className="h-9 text-xs"
                    >
                      <ArrowUpTrayIcon className="mr-1 h-4 w-4" /> Subir archivo
                      corregido
                    </SecondaryButton>
                  )}
                  {isInProcess && (
                    <SecondaryButton
                      onClick={() =>
                        openConfirm(
                          'Vas a cancelar la carga masiva. Las guías ya creadas quedan en el sistema; las pendientes no se procesarán. ¿Continuar?',
                          doCancel,
                        )
                      }
                      disabled={actionBusy}
                      className="h-9 text-xs"
                    >
                      <XMarkIcon className="mr-1 h-4 w-4" /> Cancelar
                    </SecondaryButton>
                  )}
                  {/* Con manifiesto de despacho creado NO se ofrece borrar. El
                      backend lo rechaza igual (error 15), pero mostrar un botón
                      que siempre va a fallar es peor que no mostrarlo: el
                      operador aprende a ignorar los errores. */}
                  {flags.canDeleteHeader && !hasManifest(header) && (
                    <SecondaryButton
                      onClick={() => {
                        setDeleteReason('')
                        setDeleteOpen(true)
                      }}
                      disabled={actionBusy || deleteSubmitting}
                      className="h-9 text-xs"
                    >
                      <TrashIcon className="mr-1 h-4 w-4" /> Eliminar
                    </SecondaryButton>
                  )}
                  {header.source_file_path && (
                    <SecondaryButton
                      onClick={doDownload}
                      disabled={actionBusy}
                      className="h-9  text-xs"
                      title={
                        header.source_file_name
                          ? `Descargar ${header.source_file_name}`
                          : 'Descargar archivo'
                      }
                    >
                      <ArrowDownTrayIcon className="mr-1 h-4 w-4" /> Descargar
                      archivo
                    </SecondaryButton>
                  )}

                  {/* Impresión: también en Finalizado con errores. Imprimir las
                      guías que SÍ se crearon es inocuo, a diferencia del
                      manifiesto, que se gatilla solo en Finalizado.

                      Alcance = TODA la carga. Para imprimir un subconjunto está
                      la barra de selección al pie de la tabla.

                      Por eso desaparece apenas se marca una guía: con la barra
                      de selección abajo, dos botones "Imprimir" en pantalla con
                      alcances distintos (todo vs. lo seleccionado) hacen que el
                      operador imprima la carga entera creyendo que imprime lo
                      que marcó. Al desmarcar todo, vuelve. */}
                  {canPrint &&
                    printableRows.length > 0 &&
                    selectedPrintRows.length === 0 && (
                      <Dropdown
                        label="Imprimir"
                        icon={PrinterIcon}
                        items={printMenuItems(printableRows)}
                        // Mismos colores que `SecondaryButton`: este dropdown vive en la
                        // fila de acciones de la cabecera, entre botones secundarios.
                        // `buttonClassname` (la base compartida) NO trae fondo ni color
                        // de letra —los pone cada botón—, así que sin esto quedaba
                        // transparente y heredaba el color del contenedor.
                        buttonClassName="bg-white text-main-500 hover:bg-main-50 hover:text-main-600 focus-visible:outline-main-400 !py-1.5 !text-xs h-9 items-center"
                        menuClassName="!right-0"
                      />
                    )}

                  {/* Crear manifiesto: solo Finalizado, sin manifiesto previo,
                      con guías reales y sobre una carga del cliente activo. */}
                  {isFinalized &&
                    !hasManifest(header) &&
                    bulkGuides.length > 0 && (
                      <SecondaryButton
                        onClick={askCreateManifest}
                        disabled={actionBusy || !ownerMatches}
                        title={
                          !ownerMatches
                            ? 'Esta carga masiva pertenece a otro cliente. Cambiá de cliente en el encabezado para poder despacharla.'
                            : undefined
                        }
                        className="h-9 text-xs"
                      >
                        <TruckIcon className="mr-1 h-4 w-4" /> Crear Manifiesto
                      </SecondaryButton>
                    )}

                  {/* El Nº y la fecha del manifiesto viven en el panel de info
                      (se despliega al hacer click), no acá: como chip al lado de
                      los botones competían por atención con las acciones. Queda
                      solo la acción. */}
                  {isFinalized &&
                    hasManifest(header) &&
                    header.manifest_code && (
                      <SecondaryButton
                        onClick={() => {
                          // El código va ANTES de abrir: el efecto de
                          // DispatchPrintModal tiene deps [typeUrl, open] y no
                          // reacciona a un cambio posterior de numberDispatch.
                          setPrintCode(header.manifest_code!)
                          setPrintOpen(true)
                        }}
                        className="h-9 text-xs"
                      >
                        <EyeIcon className="mr-1 h-4 w-4" /> Ver manifiesto
                      </SecondaryButton>
                    )}
                </div>
              </div>

              {/* Panel desplegable (click): crece hacia abajo sin alterar la fila,
                  con tope de altura y scroll para no romper la vista. */}
              {infoOpen && (
                <div className="mt-3 max-h-64 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3">
                  {renderHeaderInfo()}
                </div>
              )}

              {/* Anulación en curso. Va acá, junto al badge de estatus, porque es
                  donde el operador ya mira para saber en qué anda la carga.
                  Desaparece solo cuando llega el estatus final. */}
              {isAnnulling && (
                <div className="mt-3 rounded border border-main-100 bg-main-50 p-3">
                  <p className="text-sm font-medium text-main-600">
                    Anulando guías… {annulledSoFar} de {annulCount}
                  </p>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white">
                    <div
                      className="h-full rounded-full bg-main-500 transition-all duration-300"
                      style={{
                        width: `${annulCount > 0 ? Math.min(100, Math.round((annulledSoFar / annulCount) * 100)) : 0}%`,
                      }}
                    />
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Puedes cerrar esta pantalla, el proceso sigue.
                  </p>
                </div>
              )}

              {/* Guías que NO se pudieron anular. Es el resultado que más importa
                  de todo el borrado — siguen activas y facturables — y antes sólo
                  existía en el panel de logs, que es una lista cronológica que
                  nadie abre para preguntarse "¿quedó algo vivo?". */}
              {skippedShipments.length > 0 && (
                <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm font-semibold text-amber-800">
                    {skippedShipments.length} guía
                    {skippedShipments.length === 1 ? '' : 's'} no se
                    {skippedShipments.length === 1 ? ' pudo' : ' pudieron'}{' '}
                    anular
                  </p>
                  <p className="mt-1 text-xs text-amber-700">
                    Siguen activas y facturables. Requieren revisión manual.
                  </p>
                  <ul className="mt-2 space-y-1 text-xs text-amber-800">
                    {skippedShipments.map((d) => (
                      <li key={d.shipment_bulk_upload_detail_id}>
                        ·{' '}
                        <span className="font-mono">
                          {d.shipment_number?.trim() ||
                            detailLabel(d, rowPartnerId(d))}
                        </span>
                        {d.error_message ? ` — ${d.error_message}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* El manifiesto se creó pero la marca falló. Persistente: el
                operador necesita el Nº para conciliar y tiene que seguir viéndolo
                después de cerrar el modal. */}
            {/* Marca pendiente. NO se puede descartar con una X: mientras la
                carga siga sin marcar, el botón "Crear Manifiesto" vuelve a
                aparecer y quien lo apriete genera un SEGUNDO manifiesto para las
                mismas guías. El banner desaparece solo cuando el servidor
                confirma que quedó marcada. */}
            {pendingMark && !hasManifest(header) && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                <p className="font-semibold">
                  El manifiesto Nº {pendingMark.code} se creó correctamente,
                  pero esta carga masiva no quedó marcada como manifestada.
                </p>
                <p className="mt-1 text-red-700">
                  {markError ?? pendingMark.lastError}
                </p>
                <p className="mt-1 text-xs text-red-600">
                  Mientras no se marque, el botón "Crear Manifiesto" sigue
                  disponible y usarlo generaría un segundo manifiesto para las
                  mismas guías. Si el reintento no funciona, reportalo a soporte
                  con el número {pendingMark.code}.
                </p>
                <div className="mt-3">
                  <SecondaryButton
                    onClick={retryMarkManifest}
                    disabled={markRetrying}
                    className="h-9 text-xs"
                  >
                    <ArrowPathIcon
                      className={`mr-1 h-4 w-4 ${
                        markRetrying ? 'animate-spin' : ''
                      }`}
                    />
                    {markRetrying
                      ? 'Reintentando…'
                      : 'Reintentar actualizar manifiesto'}
                  </SecondaryButton>
                </div>
              </div>
            )}

            {/* Banner reconectando (CRITICAL GAP: nunca congela en silencio) */}
            {connectionLost && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800"
                aria-live="polite"
              >
                Reconectando… mostrando el último progreso conocido.
              </div>
            )}

            {/* Progreso */}
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <ImportProgressBar progress={progress} />
              {isFinalizedWithErrors && (
                <p className="mt-3 text-sm font-medium text-amber-700">
                  ⚠ {executedCount} de {header.total_details} guías creadas.{' '}
                  {errorCount} con error.
                </p>
              )}
            </div>

            {/* Toolbar: buscador + page-size + export */}
            <div
              ref={toolbarRef}
              className="flex flex-wrap items-center justify-between gap-2"
            >
              <div className="w-full sm:max-w-xs">
                <FormText
                  id="detail-search"
                  name="detail-search"
                  value={detailSearch}
                  placeholder="Buscar en cualquier columna…"
                  containerClassname="flex flex-1"
                  onChange={(e: any) => {
                    setDetailSearch(e.target.value)
                    setDetailPage(0)
                  }}
                />
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center text-xs text-gray-600">
                  <span>Mostrar</span>
                  <div className="mx-1 inline">
                    <FormSelect
                      id="detail-page-size"
                      name="detail-page-size"
                      label=""
                      selected={detailPageSize}
                      options={[10, 25, 50, 100]}
                      className="!h-8 !w-20 !px-2"
                      optionString={(o) => o.toString()}
                      onSelectOption={(o) => {
                        setDetailPageSize(o)
                        setDetailPage(0)
                      }}
                    />
                  </div>
                  <span>registros</span>
                </div>
                <div className="flex items-center gap-2">
                  <Dropdown
                    align="left"
                    label="Exportar"
                    items={[
                      {
                        items: [
                          {
                            label: 'EXCEL',
                            icon: (props) => (
                              <Icon
                                icon={fileExcelO}
                                className={props.className}
                              />
                            ),
                            onClick: () => {
                              if (!filteredDetails.length) return
                              // Solo en el Excel: `exportTableXLSX` escribe las
                              // celdas crudas y los valores de las filas
                              // pendientes vienen tal cual del archivo que subió
                              // el operador, así que un `=…` se abriría como
                              // fórmula. El sanitizador antepone una comilla, que
                              // en PDF y TXT sería basura visible sin ningún
                              // riesgo que evitar — por eso no va en el builder
                              // compartido, y acá se aplica solo a las columnas
                              // que pueden traer texto del operador.
                              exportTableXLSX(
                                DETAIL_EXPORT_COLUMNS,
                                buildDetailExportRows().map((row) =>
                                  row.map((c, i) =>
                                    typeof c === 'string' &&
                                    DETAIL_EXPORT_UNSAFE_COLUMNS.indexOf(i) !==
                                      -1
                                      ? sanitizeForExcel(c)
                                      : c,
                                  ),
                                ),
                                'Detalle',
                                `CargaMasiva_${header.bulk_upload_number}`,
                              )
                            },
                          },
                          {
                            label: 'PDF',
                            icon: (props) => (
                              <Icon
                                icon={filePdfO}
                                className={props.className}
                              />
                            ),
                            onClick: () => {
                              if (!filteredDetails.length) return
                              exportTablePDF(
                                user.user!,
                                `CARGA MASIVA ${header.bulk_upload_number}`,
                                DETAIL_EXPORT_COLUMNS,
                                buildDetailExportRows(),
                                `CargaMasiva_${header.bulk_upload_number}`,
                              )
                            },
                          },
                          {
                            label: 'TXT',
                            icon: (props) => (
                              <Icon
                                icon={fileTextO}
                                className={props.className}
                              />
                            ),
                            onClick: () => {
                              if (!filteredDetails.length) return
                              exportTableTXT(
                                DETAIL_EXPORT_COLUMNS,
                                buildDetailExportRows(),
                                `CargaMasiva_${header.bulk_upload_number}`,
                              )
                            },
                          },
                        ],
                      },
                    ]}
                    buttonClassName="bg-main-500 hover:bg-main-400 hover:border-main-400 text-white focus-visible:outline-main-400 !pt-1 !pb-1 !text-xs !ring-0 h-[32px] items-center"
                    menuClassName="!left-0 !top-full !mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Tabla de detalles */}
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-500">
                  <tr>
                    {canPrint && (
                      <th
                        className="px-3 py-2 font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {filteredPrintableRows.length > 0 && (
                          <FormCheckbox
                            id="select-all-printable"
                            name="select-all-printable"
                            label=""
                            checked={allFilteredSelected}
                            onChange={toggleAllFiltered}
                          />
                        )}
                      </th>
                    )}
                    <th className="px-3 py-2 font-medium">Nº</th>
                    <th className="px-3 py-2 font-medium">Estatus</th>
                    <th className="px-3 py-2 font-medium">Nº de guía</th>
                    <th className="px-3 py-2 font-medium">Nº de guía aliada</th>
                    <th className="px-3 py-2 font-medium">Mensaje</th>
                    <th className="px-3 py-2 font-medium">
                      Fecha de procesado
                    </th>
                    <th className="px-3 py-2 font-medium text-right">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedDetails.length === 0 && (
                    <tr>
                      <td
                        colSpan={canPrint ? 8 : 7}
                        className="px-3 py-8 text-center text-sm text-gray-500"
                      >
                        {detailSearch
                          ? 'No hay detalles que coincidan con la búsqueda.'
                          : 'No hay detalles.'}
                      </td>
                    </tr>
                  )}
                  {paginatedDetails.map((d, rowIndex) => {
                    const dm = detailStatusMeta(d.status_name, d.status_id)
                    const printRow = printableById.get(
                      d.shipment_bulk_upload_detail_id,
                    )
                    const rowSelected = printRow
                      ? isSelected(printRow.detailId)
                      : false
                    const canonical = resolveDetailStatusName(
                      d.status_name,
                      d.status_id,
                    )
                    const isExec = canonical === ImportDetailStatusName.Executed
                    const isErrServer =
                      canonical === ImportDetailStatusName.ErrorServer
                    // Filas con payload que vale la pena inspeccionar (errores + sustituida).
                    const isErrorish =
                      canonical === ImportDetailStatusName.Error ||
                      canonical === ImportDetailStatusName.ErrorProcessing ||
                      canonical === ImportDetailStatusName.ErrorServer ||
                      canonical === ImportDetailStatusName.Substituted
                    // Helper compartido, NO una copia local. Esta línea tenía su
                    // propia regla (`!== Executed`) y por eso se quedó atrás: no
                    // excluía "En ejecución", y cuando se agregaron Anulada y
                    // Eliminada el icono de basura siguió apareciendo sobre filas
                    // que ya estaban resueltas por un borrado.
                    const canDelete = canDeleteDetailStatus(
                      d.status_name,
                      d.status_id,
                    )
                    return (
                      <tr
                        key={d.shipment_bulk_upload_detail_id}
                        className={[
                          'hover:bg-gray-200',
                          rowIndex % 2 === 0 ? 'bg-gray-100' : '',
                          rowSelected ? '!bg-blue-50' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {/* Solo llevan checkbox las filas que YA tienen guía: una fila
                            con error no tiene nada que imprimir, y darle checkbox haría
                            que el contador de la barra mienta. */}
                        {canPrint && (
                          <td
                            className={
                              printRow
                                ? 'px-3 py-2 select-none cursor-pointer'
                                : 'px-3 py-2'
                            }
                            title={
                              printRow
                                ? 'Shift + click para marcar un rango'
                                : undefined
                            }
                            // La celda es la que decide porque necesita `shiftKey` y el
                            // índice de fila; el input queda controlado por `checked`.
                            // Mismo patrón que ShipmentTable, que además hace que la
                            // barra espaciadora funcione: el navegador dispara un click
                            // sintético sobre la celda al activar el checkbox.
                            onClick={(e) =>
                              printRow &&
                              toggleRow(printRow.detailId, rowIndex, e.shiftKey)
                            }
                          >
                            {printRow && (
                              <FormCheckbox
                                id={`select-${d.shipment_bulk_upload_detail_id}`}
                                name={`select-${d.shipment_bulk_upload_detail_id}`}
                                label=""
                                checked={rowSelected}
                                onChange={() => {}}
                              />
                            )}
                          </td>
                        )}
                        <td className="px-3 py-2 tabular-nums text-gray-700">
                          {d.line_number}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${dm.badgeClass}`}
                          >
                            {dm.label}
                          </span>
                          {isErrServer && (
                            <div className="mt-0.5 text-[11px] text-gray-400">
                              intento {d.retry_count ?? 0}/
                              {SERVER_ERROR_RETRY_CAP}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-700">
                          {d.shipment_number ?? (
                            <span className="text-gray-400">–</span>
                          )}
                        </td>
                        {/* Nº de guía aliada: con fallback al payload, así las filas
                            pendientes y con error dejan de salir vacías. `break-all`
                            + ancho tope para que un id largo envuelva en vez de
                            ensanchar la tabla entera. */}
                        <td className="px-3 py-2 font-mono text-xs text-gray-700 break-all max-w-[12rem]">
                          {rowPartnerId(d) ?? (
                            <span className="text-gray-400">–</span>
                          )}
                        </td>
                        <td
                          className="px-3 py-2 text-red-600 max-w-xs break-words whitespace-normal align-top"
                          title={d.error_message ?? undefined}
                        >
                          {d.error_message ?? '–'}
                        </td>
                        <td className="px-3 py-2 tabular-nums text-gray-500">
                          {formatDateTime(d.executed_date)}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex justify-end gap-2">
                            {isExec && (
                              <button
                                title="Ver guía"
                                onClick={() =>
                                  navigate(
                                    d.shipment_number
                                      ? `/shipments/consult?query=${encodeURIComponent(
                                          d.shipment_number,
                                        )}`
                                      : '/shipments/consult',
                                  )
                                }
                                className="text-main-500 hover:text-main-400"
                              >
                                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                              </button>
                            )}
                            {isErrorish && (
                              <button
                                title="Ver payload"
                                onClick={() => openPayload(d)}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                title="Eliminar este detalle"
                                onClick={() =>
                                  openConfirm(
                                    `¿Eliminar la guía ${detailLabel(
                                      d,
                                      rowPartnerId(d),
                                    )}? No se procesará.`,
                                    () =>
                                      doDeleteDetail(
                                        d.shipment_bulk_upload_detail_id,
                                      ),
                                  )
                                }
                                disabled={actionBusy}
                                className="text-gray-500 hover:text-red-600"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <PaginationFooter
              currentPage={detailPage}
              itemsPerPage={detailPageSize}
              totalItems={filteredDetails.length}
              onPageChange={setDetailPage}
            />

            {/* Barra de acciones de selección múltiple. Mismo patrón (y mismos
                textos) que la de consulta de guías: es sticky al fondo, reserva su
                propio espacio en vez de empujar la tabla, y deja los dos formatos
                más usados a un solo clic. */}
            {selectedPrintRows.length > 0 && (
              <div className="sticky bottom-0 z-20 -mx-4 mt-6 px-4 py-3 bg-white border-t border-gray-200 shadow-[0_-2px_8px_rgba(0,0,0,0.04)] flex flex-col sm:flex-row sm:items-center gap-3">
                <span className="text-sm font-semibold text-gray-900">
                  {selectedPrintRows.length === 1
                    ? '1 guía seleccionada'
                    : `${selectedPrintRows.length} guías seleccionadas`}
                </span>

                <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
                  <PrimaryButton
                    className="!py-1.5 !text-xs items-center gap-1.5"
                    onClick={() => startPrint('pdf', selectedPrintRows)}
                  >
                    <PrinterIcon className="w-4 h-4" />
                    Etiqueta PDF 10x10
                  </PrimaryButton>

                  <PrimaryButton
                    className="!py-1.5 !text-xs items-center gap-1.5"
                    title="Se imprimen hasta 4 etiquetas por hoja carta para guías multipiezas"
                    onClick={() => startPrint('pdf-letter', selectedPrintRows)}
                  >
                    <PrinterIcon className="w-4 h-4" />
                    Etiqueta PDF Carta
                  </PrimaryButton>

                  <Dropdown
                    label="Más"
                    items={[
                      {
                        items: [
                          {
                            label: 'Documento Bolsillo',
                            icon: PrinterIcon,
                            onClick: () =>
                              startPrint('pocket', selectedPrintRows),
                          },
                          {
                            label: 'Etiqueta Zebra 10x6',
                            icon: PrinterIcon,
                            onClick: () =>
                              startPrint('zebra-10x6', selectedPrintRows),
                          },
                          {
                            label: 'Etiqueta Zebra 10x10',
                            icon: PrinterIcon,
                            onClick: () =>
                              startPrint('zebra-10x10', selectedPrintRows),
                          },
                        ],
                      },
                    ]}
                    buttonClassName="!py-1.5 !text-xs bg-white text-main-500 items-center"
                    // Se abre hacia arriba: la barra vive al fondo del viewport, así
                    // que hacia abajo el menú quedaría fuera de pantalla.
                    menuClassName="!bottom-full !top-auto !mt-0 !mb-2 origin-bottom-right"
                  />

                  <SecondaryButton
                    className="!py-1.5 !text-xs items-center !border-gray-200 !text-gray-500"
                    onClick={clearSelection}
                  >
                    Limpiar
                  </SecondaryButton>
                </div>
              </div>
            )}

            {/* Logs (últimos 200, solo WARNING/ERROR).

                Cada línea se ancla a SU fila: sin eso el operador leía el motivo
                del fallo sin poder decir de qué guía era, que es exactamente lo
                que necesita para reportarlo. El orden de la línea sigue el orden
                en que se lee: tipo · identidad · mensaje · fecha (la fecha es el
                dato menos usado y por eso queda al final). */}
            {header.logs && header.logs.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h2 className="mb-2 text-sm font-semibold text-gray-700">
                  Logs
                </h2>
                <p className="mb-2 text-xs text-gray-500">
                  {logsCaption(header.logs.length)}
                </p>
                {/* Encabezado de columnas: sin él las anchuras fijas de abajo se
                    leen como una cuadrícula arbitraria en vez de como una tabla. */}
                <div className="hidden border-b border-gray-200 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 md:grid md:grid-cols-[3.5rem_18rem_minmax(0,1fr)_10rem_9rem] md:gap-x-3 md:px-2">
                  <span>Tipo</span>
                  <span>Identidad</span>
                  <span>Mensaje</span>
                  <span>Usuario</span>
                  <span>Fecha</span>
                </div>
                <ul className="text-xs">
                  {header.logs.map((l, logIndex) => {
                    const entry = detailIndex.get(
                      detailKey(l.shipment_bulk_upload_detail_id),
                    )
                    const detail = entry?.detail
                    const staleReason = logStaleReason(detail)
                    const partnerId = entry?.partnerId ?? null
                    const label = logRowLabel(l, detail, partnerId)
                    // Las dos filas del borrado llegan con el marcador que el
                    // backend necesita para recuperarse. El operador no tiene
                    // por qué leer `[anulacion-encolada] final_status=72`.
                    const logMessage =
                      annulmentLogView(l.message)?.message ?? l.message
                    const staleTitle =
                      staleReason === 'resolved'
                        ? 'Resuelto: la guía ya se creó'
                        : staleReason === 'requeued'
                          ? 'La guía se volvió a encolar; este error es del intento anterior'
                          : undefined
                    // El prefijo para lector de pantalla dice lo MISMO que el
                    // tooltip: una fila re-encolada todavía puede fallar, así que
                    // anunciarla como "Resuelto" sería mentir.
                    const staleSrLabel =
                      staleReason === 'resolved'
                        ? 'Resuelto: '
                        : staleReason === 'requeued'
                          ? 'Reencolado: '
                          : null
                    return (
                      <li
                        key={l.shipment_bulk_upload_log_id}
                        className={[
                          'grid grid-cols-1 gap-x-3 gap-y-0.5 px-2 py-1 text-xs',
                          'md:grid-cols-[3.5rem_18rem_minmax(0,1fr)_10rem_9rem] md:items-baseline',
                          'hover:bg-gray-200',
                          logIndex % 2 === 0 ? 'bg-gray-100' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <span
                          className={`font-semibold ${
                            staleReason
                              ? 'text-gray-500'
                              : l.event_type === 'ERROR'
                                ? 'text-red-600'
                                : 'text-amber-600'
                          }`}
                        >
                          {logEventLabel(l.event_type)}
                        </span>
                        {partnerId ? (
                          <button
                            type="button"
                            className="min-w-0 truncate text-left font-mono text-main-500 underline decoration-dotted underline-offset-2 hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-main-500 rounded px-1 -mx-1"
                            title={label}
                            // El nombre accesible ARRANCA con el texto visible:
                            // WCAG 2.5.3 (Label in Name) exige que lo contenga, o
                            // el usuario de control por voz no puede activar el
                            // botón diciendo lo que ve.
                            aria-label={`${label} — filtrar la tabla por ${partnerId}`}
                            onClick={() => {
                              setDetailSearch(partnerId)
                              setDetailPage(0)
                              toolbarRef.current?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start',
                              })
                              // `FormText` declara `ref` como prop común de un FC,
                              // así que React lo descarta y nunca llega al input:
                              // se enfoca por el id que ya tiene el campo.
                              //
                              // `preventScroll`: enfocar un input fuera de pantalla
                              // lo trae a la vista de golpe y CANCELA el scroll
                              // suave de arriba.
                              document
                                .getElementById('detail-search')
                                ?.focus({ preventScroll: true })
                            }}
                          >
                            {label}
                          </button>
                        ) : (
                          <span
                            className={`min-w-0 truncate font-mono ${
                              staleReason ? 'text-gray-500' : 'text-gray-700'
                            }`}
                            title={label}
                          >
                            {label}
                          </span>
                        )}
                        {/* El mensaje baja de línea en vez de recortarse. Con el
                            grid las columnas siguen alineadas aunque la fila crezca
                            — que era el problema real del `flex` anterior — así que
                            no hace falta ni recortar ni un botón para expandir. */}
                        <span
                          className={`min-w-0 break-words ${
                            staleReason ? 'text-gray-500' : 'text-gray-600'
                          }`}
                          title={staleTitle}
                        >
                          {staleSrLabel && (
                            <span className="sr-only">{staleSrLabel}</span>
                          )}
                          {logMessage}
                        </span>
                        {/* Quién lo hizo. El backend siempre lo guarda en
                            `creation_user`, pero la fila nunca lo mostraba: para
                            un borrado con motivo, saber el motivo sin saber quién
                            lo escribió deja la traza a medias.

                            La celda se renderiza SIEMPRE, vacía si hace falta: si
                            desaparece cuando el log no tiene usuario, la fecha se
                            corre y las filas dejan de alinearse entre sí. */}
                        <span
                          className="min-w-0 truncate text-gray-500"
                          title={l.creation_user ?? undefined}
                        >
                          {l.creation_user_name || l.creation_user || ''}
                        </span>
                        <span className="tabular-nums text-gray-400 md:text-right">
                          {formatDateTime(l.log_date)}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>
        )
      )}

      <ModalQuestion
        open={confirmOpen}
        title={confirmTitle}
        setOpen={setConfirmOpen}
        setAccepts={onAccepts}
      />

      {/* Modal de borrado — instancia PROPIA, no la compartida de arriba.
          Aquella sirve a ~8 confirmaciones distintas con un `() => void` sin
          parámetros; ésta necesita capturar un motivo y validar antes de dejar
          confirmar. */}
      <ModalQuestion
        open={deleteOpen}
        setOpen={setDeleteOpen}
        setAccepts={(value) => {
          if (value === true) doDelete(deleteReason.trim())
        }}
        acceptTone="danger"
        acceptDisabled={
          deleteSubmitting || deleteReason.trim().length < DELETE_REASON_MIN
        }
        acceptLabel={
          annulCount > 0
            ? `Eliminar y anular ${annulCount} guía${annulCount === 1 ? '' : 's'}`
            : 'Eliminar carga'
        }
        cancelLabel="Cancelar"
        icon={
          <ExclamationTriangleIcon className="h-14 w-14 shrink-0 text-red-600" />
        }
        title={
          annulCount > 0
            ? `Se anularán ${annulCount} guía${annulCount === 1 ? '' : 's'}`
            : 'Se eliminará esta carga masiva'
        }
      >
        <p className="-mt-2 mb-4 text-center text-sm text-gray-600">
          Esta carga masiva y todas sus guías creadas se eliminarán.
          <br />
          No se puede deshacer.
        </p>
        <label
          htmlFor="bulk-delete-reason"
          className="block text-sm font-medium text-gray-700"
        >
          Motivo de la eliminación <span className="text-red-600">*</span>
        </label>
        <textarea
          id="bulk-delete-reason"
          autoFocus
          rows={3}
          maxLength={DELETE_REASON_MAX}
          value={deleteReason}
          onChange={(e) => setDeleteReason(e.target.value)}
          aria-describedby="bulk-delete-reason-help"
          placeholder="Ej.: se cargó el archivo equivocado del cliente"
          className="mt-1 w-full rounded border border-gray-300 p-2 text-sm focus:border-main-500 focus:outline-none focus:ring-1 focus:ring-main-500"
        />
        {/* El aviso de mínimo va junto al contador y no escondido: un botón
            deshabilitado tiene que explicarse antes de frustrar, y el lector de
            pantalla lo alcanza por `aria-describedby`. */}
        <div
          id="bulk-delete-reason-help"
          className="mt-1 flex justify-between text-xs"
        >
          <span className="text-gray-500">
            Mínimo {DELETE_REASON_MIN} caracteres
          </span>
          <span className="text-gray-400">
            {deleteReason.length}/{DELETE_REASON_MAX}
          </span>
        </div>
      </ModalQuestion>

      {/* Sin `timeOut` corto: `ModalLoading` auto-cierra también en ERROR, no
          sólo en SUCCESS. Con 4s el operador perdía de vista mensajes que tiene
          que leer y accionar ("ya tiene manifiesto", "motivo demasiado corto").
          El default de 15s le da tiempo; el éxito igual se cierra solo. */}
      <ModalLoading
        open={deleteLoadingOpen}
        title={deleteLoadingTitle}
        status={deleteLoadingStatus}
        setOpen={setDeleteLoadingOpen}
      />

      {/* Modal de despacho: el MISMO flujo de /Dispatch/create, con las guías de
          esta carga preseleccionadas.

          `Modal` y no `ModalWidthFull`: este usa overflow-visible y aquel
          overflow-hidden, y el combobox de Rutas de CreateDataDispatch es
          `absolute top-full` SIN portal, así que dentro de ModalWidthFull se
          recorta. `w-full` es obligatorio: el panel es flex item con width:auto
          y sin él se encoge al contenido. 80rem = 1280px = el max content width
          de DESIGN.md. */}
      <Modal
        open={manifestOpen}
        setOpen={setManifestOpen}
        className="w-full max-w-[95vw] flex flex-col"
        style={{ maxWidth: '80rem', minHeight: '80vh' }}
        staticBackdrop
        initialFocus={manifestFocusRef}
        onEscape={() => setManifestOpen(false)}
      >
        <div
          ref={manifestFocusRef as React.RefObject<HTMLDivElement>}
          tabIndex={-1}
          className="mb-4 flex items-start justify-between border-b border-gray-200 pb-3 outline-none"
        >
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Creación de manifiesto de despacho
            </h2>
            <p className="text-sm text-gray-500">
              Carga masiva {header?.bulk_upload_number}
            </p>
          </div>
          <button
            onClick={() => setManifestOpen(false)}
            aria-label="Cerrar"
            className="rounded p-1 hover:bg-gray-100"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {manifestOpen && (
          <DispatchCreationFlow
            embedded
            preselectedGuides={bulkGuides}
            onClose={() => setManifestOpen(false)}
            onNoUsableGuides={handleNoUsableGuides}
            onCreated={handleManifestCreated}
            onRequestPrint={(code) => {
              setPrintCode(code)
              setPrintOpen(true)
            }}
          />
        )}
      </Modal>

      {/* Vive acá y no dentro del flujo a propósito: el flujo se desmonta al
          cerrarse, y este aviso es justamente el que tiene que quedar. */}
      <MissingGuidesModal
        open={noUsableGuides !== null}
        onClose={() => setNoUsableGuides(null)}
        requested={noUsableGuides?.notInList ?? []}
        withoutPieces={noUsableGuides?.withoutPieces ?? []}
        allUnusable
        results={noUsableResults}
        loading={noUsableLoading}
        error={noUsableError}
      />

      {/* Aviso de éxito del manifiesto. Sobrevive al cierre del modal de
          creación, que es justo el punto: el flujo se cierra al guardar y el
          operador se queda con el número y la opción de imprimir. */}
      <ModalLoading
        open={!!manifestSuccess}
        title={manifestSuccess?.message ?? ''}
        status={ConfirmModalStatus.PRINT}
        setOpen={() => setManifestSuccess(null)}
        onPrint={() => {
          // El código va ANTES de abrir: el efecto de DispatchPrintModal tiene
          // deps [typeUrl, open] y no reacciona a un cambio posterior.
          setPrintCode(manifestSuccess?.code ?? '')
          setManifestSuccess(null)
          setPrintOpen(true)
        }}
      />

      <DispatchPrintModal
        open={printOpen}
        numberDispatch={printCode}
        setOpen={setPrintOpen}
      />

      {/* Montado bajo condición: `useShipmentLabel` dispara el POST según `open`,
          y el modal de despacho y este pelean por el iframe si conviven. */}
      {labelOpen && (
        <ShipmentLabelDjangoModal
          open={labelOpen}
          setOpen={(v) => {
            setLabelOpen(v)
            printBusyRef.current = false
          }}
          shipment={labelSlice}
          format={labelFormat}
          batch={{
            index: labelBatch,
            total: printGroupCount,
            onChange: setLabelBatch,
          }}
          onTimeout={handleLabelTimeout}
        />
      )}

      {/* Documento Bolsillo. Montado bajo condición por lo mismo: escribe en su
          propio iframe, pero si conviviera con otro visor abierto el operador
          tendría dos PDF peleando por la pantalla. */}
      {pocketOpen && (
        <ShipmentMultiPrintModal
          open={pocketOpen}
          setOpen={(v) => {
            setPocketOpen(v)
            printBusyRef.current = false
          }}
          shipment={pocketSlice}
          type={0}
          url={'/v1/Shipment/DownloadAllDocumentDeclared'}
          onPrint={() => {}}
          batch={{
            index: pocketBatch,
            total: printGroupCount,
            onChange: setPocketBatch,
          }}
        />
      )}

      {/* Progreso de la tanda Zebra.
          NO usa `ModalLoading`: su estado PROCESSING renderiza solo el spinner y el
          título, sin botones, y acá el Cancelar es obligatorio — son minutos de espera
          y el operador tiene que poder frenar sin cerrar la pestaña.
          `staticBackdrop` para que un click al costado no dé la falsa impresión de
          haber cancelado: cancelar es apretar el botón. */}
      <Modal
        open={zebraBusy}
        setOpen={() => {}}
        staticBackdrop
        style={{ maxWidth: '26rem' }}
      >
        <div className="flex flex-col items-center gap-4 py-2">
          <Spinner />
          <p
            className="text-center text-sm font-semibold text-gray-800"
            aria-live="polite"
          >
            {zebraProgress
              ? `Imprimiendo ${zebraProgress.current} de ${zebraProgress.total}...`
              : 'Imprimiendo...'}
          </p>
          <p className="text-center text-xs text-gray-500">
            Las etiquetas Zebra se mandan de a una a la impresora.
          </p>
          <SecondaryButton
            onClick={() => {
              zebraCancelRef.current = true
              setZebraCancelling(true)
            }}
            disabled={zebraCancelling}
            className="h-9 text-xs"
          >
            {zebraCancelling ? 'Cancelando…' : 'Cancelar'}
          </SecondaryButton>
        </div>
      </Modal>

      {/* Resultado de un grupo Zebra.
          NO usa `ModalLoading`: sus props `actionLabel`/`onAction` solo se renderizan en
          el estado SUCCESS, y acá el botón de "imprimir el grupo siguiente" tiene que
          aparecer también cuando el grupo terminó con fallos parciales (WARNING). Con
          `ModalLoading` un grupo parcialmente fallido dejaría al operador sin forma de
          continuar con los que faltan.
          Va por modal y no por `alertService`, cuyo host no está montado en la app. */}
      <Modal
        open={!!zebraResult}
        setOpen={() => setZebraResult(null)}
        style={{ maxWidth: '30rem' }}
      >
        <div className="flex flex-col items-center gap-4 py-2">
          {zebraResult?.status === ConfirmModalStatus.SUCCESS && (
            <CheckCircleIcon className="h-12 w-12 text-green-600" />
          )}
          {zebraResult?.status === ConfirmModalStatus.WARNING && (
            <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500" />
          )}
          {zebraResult?.status === ConfirmModalStatus.ERROR && (
            <XCircleIcon className="h-12 w-12 text-red-600" />
          )}

          <p
            className={`whitespace-pre-line break-words px-2 text-center text-sm font-semibold ${
              zebraResult?.status === ConfirmModalStatus.ERROR
                ? 'text-red-600'
                : 'text-gray-800'
            }`}
          >
            {zebraResult?.title ?? ''}
          </p>

          <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
            {zebraResult?.nextGroup != null && zebraQueue && (
              <PrimaryButton
                className="!py-1.5 !text-xs items-center gap-1.5"
                onClick={() => {
                  const { groups, kind } = zebraQueue
                  const next = zebraResult.nextGroup!
                  setZebraResult(null)
                  printBusyRef.current = true
                  void runZebraGroup(groups, next, kind)
                }}
              >
                <PrinterIcon className="h-4 w-4" />
                Imprimir grupo {zebraResult.nextGroup + 1} de{' '}
                {zebraResult.totalGroups}
              </PrimaryButton>
            )}
            <SecondaryButton
              className="!py-1.5 !text-xs items-center"
              onClick={() => setZebraResult(null)}
            >
              Cerrar
            </SecondaryButton>
          </div>

          {/* Si quedaron grupos sin imprimir y NO se ofrece continuar (cancelación o
              impresora caída), hay que decirlo: si no, el operador se va creyendo que
              imprimió todo. */}
          {zebraResult?.nextGroup == null &&
            zebraQueue &&
            zebraQueue.index + 1 < zebraQueue.groups.length && (
              <p className="px-2 text-center text-xs text-gray-500">
                Quedaron {zebraQueue.groups.length - zebraQueue.index - 1}{' '}
                grupo(s) sin imprimir. Volvé a seleccionar esas guías cuando
                quieras continuar.
              </p>
            )}
        </div>
      </Modal>

      <Modal
        open={payloadOpen}
        setOpen={setPayloadOpen}
        style={{ maxWidth: '40rem' }}
      >
        <div className="flex items-center justify-between border-b border-gray-200 pb-2">
          <h2 className="text-base font-semibold text-gray-800">
            {payloadLabel ? `Guía · ${payloadLabel}` : 'Guía (payload)'}
          </h2>
          <button onClick={() => setPayloadOpen(false)}>
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <pre className="mt-3 max-h-96 overflow-auto rounded bg-gray-50 p-3 text-xs text-gray-700">
          {payloadText}
        </pre>
      </Modal>
    </MainPage>
  )
}

export default ShipmentBulkUploadDetail
